/**
 * Typed model mutations — the engine half of WYSIWYG editing
 * (docs/superpowers/plans/2026-07-07-wysiwyg-model-editing.md §2/§3/§4a).
 *
 * A board/inspector gesture becomes ONE typed op; this module synthesizes the
 * minimal {@link TextEdit}s that realize it, reading spans off the AUTHORED
 * AST only (`ResolvedModule.decl` — never the stamped/resolved body, whose
 * member spans may live in a template's file). Application, generation
 * guarding, and undo live in the app layer; everything here is pure
 * (src, target, op) → edits.
 *
 * §4a preconditions are enforced with typed refusals — a caller bug can
 * surface as a disabled affordance or a materialize/promote precursor, never
 * as a corrupt write:
 *   - value/insert ops on a `stub` target refuse → compose
 *     {@link promoteStubWithMember} instead;
 *   - bodyless targets materialize the body IN THE SAME fragment;
 *   - inherited members can't be span-deleted → `removeField` emits
 *     `drop <path>`, `removeAspect` refuses (aspect drop is a W1 decision).
 */

import type { ModuleDecl, QualifiedName, SourceSpan, StableId } from "./types.js";
import {
  flowBodyAnchors,
  insertBodyMember,
  insertFlowMemberEdit,
  insertViewMemberEdit,
  materializeBodyEdit,
  prependBodyMember,
  removeMemberEdits,
  scaffoldEdgeEdit,
  setFieldCellEdit,
  upsertPinEdit,
  upsertStyleClauseEdit,
  upsertViewInstance,
  viewMemberAnchors,
  type TextEdit,
  type ViewInstanceBinding,
} from "./text-patch.js";
import type { FlowClause, TableClause, ViewDeclV2 } from "./v010-ast.js";
import type { GetterSigil } from "./selector-ast.js";
import { createSpanLookup, type InterfaceLocation, type ModuleLocation, type ProcessLocation } from "./span-lookup.js";
import { applyTextEdits } from "./text-patch.js";
import { isValueExprText } from "./field-exprs.js";
import { parse } from "./parser.js";
import { RESERVED_KEYWORDS, tokenize } from "./lexer.js";
import { parseSelectorSource } from "./selector-parser.js";
import { buildNodeUniverse, evalSelector } from "./selector-eval.js";
import type { ResolvedModel, ResolvedModule } from "./resolver.js";
import { buildIndexes } from "./graph.js";
import { forEachCallHop, forEachFlow, forEachNode } from "./process-ir-walk.js";
import type { CallNode, Owner, ProcessModel, ProcessNode as ProcessIrNode } from "./process-model.js";
import { answersFan, ownerOfNode } from "./process-model-builder.js";

export type ModuleMutation =
  | { readonly op: "setDescription"; readonly text: string }
  | { readonly op: "setField"; readonly path: readonly string[]; readonly value: string }
  | { readonly op: "removeField"; readonly path: readonly string[] }
  | { readonly op: "setAspect"; readonly path: readonly string[]; readonly value: string }
  | { readonly op: "removeAspect"; readonly path: readonly string[] }
  | { readonly op: "setType"; readonly typeName: string }
  | { readonly op: "setName"; readonly name: string }
  | { readonly op: "addModule"; readonly kind: string; readonly name?: string; readonly braced?: boolean }
  | { readonly op: "addInterface"; readonly kind: string; readonly name: string }
  | { readonly op: "deleteDecl" };

export type RefusalReason =
  | "stub-target" // synthesized decl — promote first (promoteStubWithMember)
  | "multiple-descriptions" // §3.1: read-only in the inspector, edit in source
  | "aspect-not-authored" // inherited aspect removal is a W1 decision
  | "no-span" // decl carries no usable span at all
  | "cycle" // move target into its own subtree
  | "name-collision" // new parent already holds that name
  | "compound-step" // replaceStep on a block node (its body would be lost)
  | "field-not-present" // removeField on a field neither authored nor inherited
  | "has-placed-members" // deleteDecl would orphan `in <target>` placements
  | "same-parent" // no-op move into the current parent
  | "bad-style-subject" // style-op subject does not parse as a selector
  | "unknown-style-subject" // style-op ref subject resolves to no model node
  | "not-table-clause" // setTableColumns targets a clause that isn't a table
  | "table-column-range" // a `from` column index is out of range
  | "empty-table-columns" // a table needs at least one column
  | "table-column-title" // a calc column's title can't be dropped
  | "not-bpmn-flow" // flow-body edit on a non-bpmn / bodyless flow clause
  | "not-retitleable" // retitleClause on a clause whose title derives from its selector
  | "range-crosses-block" // wrapStep range spans a nesting boundary (would corrupt structure)
  | "would-not-parse" // an insert whose text is illegal at this position (e.g. a bare call in a `select` body)
  | "already-present" // addElse on an `if` that already has an `else` arm
  | "arm-not-present" // removeArm on an arm/catch that doesn't exist (or the primary if / try body — not removable this way)
  | "not-reversible-item" // setUnwind on a step that isn't a `reversible` forward item
  | "bad-morph" // morphBlock to a construct with no clean byte transform in our hands
  | "merge-await" // setMergeClause on a `parallel await …` (await implies race — can't carry join/race)
  | "merge-boolean" // set a numeric merge count over a boolean (named-branch) threshold — would drop the named merge (lossy)
  | "bad-count" // a count edit given a non-positive / non-integer count
  | "no-modifier-host" // setStepModifier on a node that carries no such modifier slot (only calls carry `as`/comment)
  | "bad-modifier" // setStepModifier `as` label that isn't a bare identifier (would not re-lex as one)
  | "bad-word" // a name/kind that would not re-lex as one bare word token (spliced verbatim, it corrupts the file)
  | "ambiguous-target"; // v0.11: a bare target names >1 landed entity across spaces — a write never first-match-wins; qualify `Space::Path`

export class MutationRefusedError extends Error {
  constructor(
    readonly reason: RefusalReason,
    message: string,
  ) {
    super(message);
    this.name = "MutationRefusedError";
  }
}

function refuse(reason: RefusalReason, message: string): never {
  throw new MutationRefusedError(reason, message);
}

// =============================================================================
// Rendering
// =============================================================================

/**
 * Render a description string literal. Plain text stays `"…"`; text with
 * newlines or quotes uses the verbatim `"""` block (indented to the member,
 * which the parser's trim+dedent reverses); text containing `"""` itself
 * falls back to an escaped `"…"` (quoted strings span lines raw).
 */
function renderDescription(text: string, memberIndent: string): string {
  if (text.includes('"""')) {
    return `"${text.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  }
  if (text.includes("\n") || text.includes('"')) {
    const lines = text.split("\n").map((l) => (l.length > 0 ? `${memberIndent}${l}` : ""));
    return `"""\n${lines.join("\n")}\n${memberIndent}"""`;
  }
  return `"${text.replace(/\\/g, "\\\\")}"`;
}

const INDENT = "    ";

/** Render a user-supplied value as source text: bare only what `parseValue`
 *  parses bare — a single non-keyword identifier, a number, a boolean, or an
 *  already-quoted string; everything else gets quoted+escaped. The bare class
 *  must mirror the value grammar exactly: a dotted path, a reserved keyword
 *  (`on`, `view`, `do`…), or a digit-led dotted segment (`v1.2.3`) emitted
 *  bare left the whole file unparseable after one cell edit (r37). Shared by
 *  the inspector, table cells, CLI, and MCP write paths. */
export function renderValueLiteral(raw: string): string {
  const v = raw.trim();
  // Pre-quoted pass-through only for a WELL-FORMED single string literal —
  // a leading quote alone ('"broken', '"He said "hi""') spliced verbatim
  // leaves an unterminated/garbage-trailed literal (r38); such text falls
  // through to the escape-quote arm like any other free text.
  if (/^"(?:[^"\\]|\\.)*"$/s.test(v)) return v;
  // A getter or cast is a VALUE EXPRESSION (§4.3.2), not text — quoting it
  // would silently turn a live rule into the literal `"@@team"`.
  if (isValueExprText(v)) return v;
  if (v === "true" || v === "false") return v;
  if (/^-?\d+(\.\d+)?$/.test(v)) return v;
  if (/^[A-Za-z_]\w*$/.test(v) && !RESERVED_KEYWORDS.has(v)) return v;
  return `"${v.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

// =============================================================================
// Module-target dispatcher
// =============================================================================

const VALUE_OPS = new Set(["setDescription", "setField", "removeField", "setAspect", "removeAspect", "setType", "setName"]);

export function synthesizeModuleMutation(src: string, loc: ModuleLocation, op: ModuleMutation): TextEdit[] {
  if (loc.materialization === "stub") {
    refuse("stub-target", `'${op.op}' on synthesized stub '${loc.decl.name}' — promote it first`);
  }
  if (!loc.span) refuse("no-span", `'${loc.decl.name}' carries no source span`);

  const decl = loc.decl;
  const bodyless = loc.materialization === "bodyless";

  switch (op.op) {
    case "setDescription": {
      const entries = decl.body.descriptions.filter((d) => d.source);
      if (entries.length > 1) {
        refuse("multiple-descriptions", `'${decl.name}' has ${entries.length} description strings — edit in source`);
      }
      const memberIndent = memberIndentOf(src, loc.span, entries[0]?.source);
      const literal = renderDescription(op.text, memberIndent);
      if (entries.length === 1) {
        const s = entries[0]!.source!;
        return [{ start: s.start.offset, end: s.end.offset, newText: literal }];
      }
      if (bodyless) return materializeBodyEdit(src, decl, [literal]);
      return prependBodyMember(src, decl, literal);
    }

    case "setField": {
      refuseOnBadPath("field", op.path);
      if (bodyless) return materializeBodyEdit(src, decl, [`${op.path.join(".")}: ${op.value}`]);
      return setFieldCellEdit(src, decl, op.path, op.value);
    }

    case "removeField": {
      const authored = decl.body.fields.find((f) => samePath(f.path, op.path));
      if (authored?.source) {
        return removeMemberEdits(src, authored.source.start.offset, authored.source.end.offset);
      }
      // Inherited (template-stamped / cascaded): the removal op is `drop` —
      // but ONLY when the RESOLVED body actually carries the field. A blind
      // `drop` on a field the module never had is itself a validation error
      // (§4.8 "drop only applies to inherited entities"; fuzz find).
      const inherited = loc.module.body.fields.some((f) => samePath(f.path, op.path));
      if (!inherited) {
        refuse("field-not-present", `'${decl.name}' has no field '${op.path.join(".")}' to remove`);
      }
      refuseOnBadPath("field", op.path); // the `drop` line splices the path verbatim (r40)
      const line = `drop ${op.path.join(".")}`;
      if (bodyless) return materializeBodyEdit(src, decl, [line]);
      return insertBodyMember(src, decl, line);
    }

    case "setAspect": {
      refuseOnBadPath("aspect", op.path);
      const authored = decl.body.aspects.find((a) => samePath(a.path, op.path));
      if (authored?.source) {
        const anchor = (authored.pathSource ?? authored.source).start.offset;
        const colon = src.indexOf(":", anchor);
        if (colon >= 0 && colon < authored.source.end.offset) {
          return [{ start: colon + 1, end: authored.source.end.offset, newText: ` ${op.value}` }];
        }
        // Valueless blank (`aspect domain` / a block-form `domain` entry):
        // no colon to anchor — FILL the entry in place instead of appending
        // a duplicate next to the blank.
        const s = authored.source;
        const keyword = src.startsWith("aspect", s.start.offset);
        const head = keyword ? `aspect ${op.path.join(".")}` : op.path.join(".");
        return [{ start: s.start.offset, end: s.end.offset, newText: `${head}: ${op.value}` }];
      }
      const line = `aspect ${op.path.join(".")}: ${op.value}`;
      if (bodyless) return materializeBodyEdit(src, decl, [line]);
      return insertBodyMember(src, decl, line);
    }

    case "removeAspect": {
      const authored = decl.body.aspects.find((a) => samePath(a.path, op.path));
      if (!authored?.source) {
        refuse("aspect-not-authored", `aspect '${op.path.join(".")}' is not authored on '${decl.name}'`);
      }
      return removeMemberEdits(src, authored.source.start.offset, authored.source.end.offset);
    }

    case "setType": {
      refuseOnBadWord("kind", op.typeName);
      const s = decl.typeSource ?? loc.span;
      return [{ start: s.start.offset, end: s.start.offset + decl.typeName.length, newText: op.typeName }];
    }

    case "setName": {
      refuseOnBadWord("name", op.name);
      if (decl.unnamed) {
        const after = (decl.idSource ?? decl.typeSource)?.end.offset;
        if (after === undefined) refuse("no-span", `unnamed '${decl.name}' has no kind span`);
        return [{ start: after, end: after, newText: ` ${op.name}` }];
      }
      const s = decl.nameSource;
      if (!s) refuse("no-span", `'${decl.name}' has no name span`);
      return [{ start: s.start.offset, end: s.end.offset, newText: op.name }];
    }

    case "addModule": {
      refuseOnBadWord("kind", op.kind);
      if (op.name) {
        refuseOnBadWord("name", op.name);
        refuseOnMemberNameCollision(loc, op.name);
      }
      const line = declLine(op.kind, op.name, op.braced);
      if (bodyless) return materializeBodyEdit(src, decl, [line]);
      return insertBodyMember(src, decl, line);
    }

    case "addInterface": {
      refuseOnBadWord("kind", op.kind);
      refuseOnBadWord("name", op.name);
      refuseOnMemberNameCollision(loc, op.name);
      const line = `${op.kind} ${op.name}`;
      if (bodyless) return materializeBodyEdit(src, decl, [line]);
      return insertBodyMember(src, decl, line);
    }

    case "deleteDecl": {
      const extent = loc.extent ?? loc.span;
      // Decls whose TEXT lives outside this extent but resolve INTO the
      // deleted subtree (`in <target-or-descendant>` placements — fuzz found
      // the descendant case) would be orphaned into a hard placement error,
      // not a stub. Refuse; the author moves or deletes them first. (Process
      // references, by contrast, degrade to draft-floor stubs by design.)
      const placed: string[] = [];
      const collectPlaced = (m: ModuleLocation["module"]): void => {
        for (const child of m.modules) {
          if ((child.decl.parentPath?.length ?? 0) > 0) placed.push(child.name);
          collectPlaced(child);
        }
      };
      collectPlaced(loc.module);
      if (placed.length > 0) {
        refuse(
          "has-placed-members",
          `deleting '${decl.name}' would orphan ${placed.length} 'in'-placed declaration(s) (${placed.join(", ")}) — move or delete them first`,
        );
      }
      return removeMemberEdits(src, extent.start.offset, extent.end.offset);
    }
  }
}

/** Refuse a name/kind that would not re-lex as ONE bare word token — an
 *  identifier or a keyword (keyword decl names are legal, §keyword-names).
 *  Spliced verbatim into a declaration position, anything else ("web
 *  service", "My Flow", stray quotes, empty text) leaves the file
 *  unparseable (r38). Checked against the REAL lexer, not a regex mirror. */
function refuseOnBadWord(slot: string, text: string): void {
  const { tokens, errors } = tokenize(text);
  const ok =
    errors.length === 0 &&
    tokens.length === 2 &&
    tokens[1]!.type === "EOF" &&
    (tokens[0]!.type === "IDENTIFIER" || RESERVED_KEYWORDS.has(tokens[0]!.value));
  if (!ok) refuse("bad-word", `${slot} '${text}' is not a single bare word — it would not re-lex`);
}

/** Refuse a field/aspect path whose segments don't EACH re-lex as ONE
 *  IDENTIFIER token — member-key positions take identifiers only (keywords,
 *  spaces, `$`-led words, empty segments spliced verbatim corrupt the file;
 *  r40). Checked against the real lexer, like {@link refuseOnBadWord}. */
function refuseOnBadPath(slot: string, path: readonly string[]): void {
  const bad =
    path.length === 0 ||
    path.some((seg) => {
      const { tokens, errors } = tokenize(seg);
      return errors.length > 0 || tokens.length !== 2 || tokens[0]!.type !== "IDENTIFIER";
    });
  if (bad) {
    refuse("bad-word", `${slot} '${path.join(".")}' is not a dotted identifier path — it would not re-lex`);
  }
}

function declLine(kind: string, name: string | undefined, braced: boolean | undefined): string {
  const head = name ? `${kind} ${name}` : kind;
  return braced ? `${head} {}` : head;
}

/**
 * Refuse an add op whose name the RESOLVED body already carries (fuzz find,
 * seed 526663547): synthesizing a second same-name member writes a §4.9
 * MULTI_OP_ON_TARGET error into the text (and, on an inherited name with a
 * different kind, TYPE_CHANGE_NEEDS_OVERRIDE). One namespace per body —
 * modules, interfaces, and surfaces all count.
 */
function refuseOnMemberNameCollision(loc: ModuleLocation, name: string): void {
  const m = loc.module;
  const taken =
    m.modules.some((c) => c.name === name) ||
    m.interfaces.some((i) => i.name === name) ||
    m.surfaces.some((s) => s.name === name);
  if (taken) {
    refuse("name-collision", `'${loc.decl.name}' already carries a member named '${name}'`);
  }
}

function samePath(a: QualifiedName | readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

/** Indent for a member of this decl's body: an existing member's own indent,
 *  else the decl's indent + one level. */
function memberIndentOf(src: string, declSpan: SourceSpan, memberSpan: SourceSpan | undefined): string {
  const anchor = memberSpan?.start.offset;
  if (anchor !== undefined) return leadingIndent(src, anchor);
  return leadingIndent(src, declSpan.start.offset) + INDENT;
}

function leadingIndent(src: string, offset: number): string {
  const lineStart = src.lastIndexOf("\n", offset - 1) + 1;
  let i = lineStart;
  while (i < offset && (src[i] === " " || src[i] === "\t")) i++;
  return src.slice(lineStart, i);
}

// =============================================================================
// Process-target ops (W2 — drawEdge lands as a process step, §3.3)
// =============================================================================

export type ProcessMutation = {
  readonly op: "appendStep";
  /** Step operands as REFERENCES resolvable in the process's scope (compute
   *  via {@link shortestUniqueSuffix}); `to` may carry an interface leaf. */
  readonly from: string;
  readonly to: string;
};

export function synthesizeProcessMutation(
  src: string,
  loc: ProcessLocation,
  op: ProcessMutation,
): TextEdit[] {
  if (loc.materialization === "stub") {
    refuse("stub-target", `'${op.op}' on a synthesized process — author it first`);
  }
  if (!loc.span) refuse("no-span", "process carries no source span");
  switch (op.op) {
    case "appendStep":
      // Parse-guarded: from/to refs are spliced verbatim, and a keyword-named
      // module (a legal decl name) is unrepresentable in step position (r40).
      return guardInsertParse(src, scaffoldEdgeEdit(src, loc.decl, op.from, op.to), op.op);
  }
}

/** Control constructs a step (or contiguous sibling range) can be wrapped in.
 *  `select` is excluded — its body is cases, not a flat flow, so a step doesn't
 *  map onto it. Each wraps textually, byte-preserving the inner steps, with
 *  draft-floor placeholders (`if TODO`, `each TODO`, empty `catch`) the author
 *  fills in. */
export type WrapConstruct = "if" | "each" | "try" | "parallel" | "reversible";

/** Which arm a {@link ProcessStepMutation} `removeArm` op drops. `else` is the
 *  trailing condition-less `else`; `elseIf` indexes `BranchNode.arms` (≥1 — index
 *  0 is the primary `if`, which is not removable this way); `catch` indexes a
 *  guard/retry node's `catches`. */
export type RemovableArm =
  | { readonly kind: "else" }
  | { readonly kind: "elseIf"; readonly index: number }
  | { readonly kind: "catch"; readonly index: number };

export type ProcessStepMutation =
  | { readonly op: "removeStep"; readonly stepId: string }
  /** Insert a step on its own line AFTER the anchor. `text` is the caller-rendered
   *  step line (chip→rowText, zero-drift); `from`/`to` are a `from > to` shorthand
   *  kept for existing callers when `text` is absent. */
  | { readonly op: "insertStepAfter"; readonly stepId: string; readonly from?: string; readonly to?: string; readonly text?: string }
  /** Insert a step on its own line BEFORE the anchor (caller-rendered line). */
  | { readonly op: "insertStepBefore"; readonly stepId: string; readonly text: string }
  /** Prepend a step inside a block node's body (for an empty branch-arm / try
   *  body / loop body that has no sibling to anchor after). */
  | { readonly op: "insertStepInto"; readonly blockStepId: string; readonly text: string }
  /** Rewrite a SIMPLE call step (`from > to`) — the owner-reassignment path.
   *  Compound blocks refuse (their bodies would be lost). */
  | { readonly op: "replaceStep"; readonly stepId: string; readonly from: string; readonly to: string }
  /** Wrap a step, or a contiguous run of sibling steps (`stepId`..`lastStepId`),
   *  in a control construct — textual wrap that re-indents the inner span one
   *  level and byte-preserves it (comments/subprocesses kept). Refuses a range
   *  that crosses a nesting boundary. */
  | { readonly op: "wrapStep"; readonly stepId: string; readonly lastStepId?: string; readonly construct: WrapConstruct }
  /** Span-scoped owner reassignment: rewrite ONLY the caller (`from`) token of
   *  one call HOP, leaving its callee and every sibling hop byte-unchanged. The
   *  `stepId` keys the HOP (`…/hop:H`, from the walk resolver), so a chain head
   *  (`A > B > C`) reassigns to `X > B > C` without a whole-span rewrite. Refuses
   *  a hop whose caller is implicit (no authored source — a later chain hop). */
  | { readonly op: "reassignStepOwner"; readonly stepId: string; readonly owner: string }
  /** Inline-edit a terminator's reason/label text (`fail "…"` / `finish "…"`),
   *  replacing ONLY the quoted-string span (the rest of the node byte-unchanged).
   *  Refuses a bodyless terminator (no reason span to edit) — adding a reason to
   *  a bare `fail` is a later insert path. */
  | { readonly op: "setTerminatorText"; readonly stepId: string; readonly text: string }
  /** Inline-edit a branch arm's condition (`if <cond>` / `else if <cond>`),
   *  replacing ONLY the condition token span. `armIndex` selects the arm
   *  (0 = `if`, 1.. = each `else if`). Refuses an arm with no authored condition
   *  (a body-only draft branch — adding one is a separate insert path). */
  | { readonly op: "setBranchCondition"; readonly stepId: string; readonly armIndex: number; readonly text: string }
  /** Inline-edit a `select <head>` head token. Refuses a headless select. */
  | { readonly op: "setSelectHead"; readonly stepId: string; readonly text: string }
  /** Inline-edit a `select` case label token; `caseIndex` selects the case. */
  | { readonly op: "setCaseLabel"; readonly stepId: string; readonly caseIndex: number; readonly text: string }
  /** Append a trailing `else { }` arm to an `if` (`stepId`). Refuses a non-branch
   *  node or an `if` that already has an `else` (the sibling-insert for `if`,
   *  plan §170). The empty arm then takes steps via the normal insert "+". */
  | { readonly op: "addElse"; readonly stepId: string }
  /** Append a trailing `catch [Error] { }` arm to a `try` (guard/retry `stepId`).
   *  Multiple catches are legal, so this never refuses on count. `errorType`
   *  bare-emits when a valid identifier, else omitted (a catch-all). */
  | { readonly op: "addCatch"; readonly stepId: string; readonly errorType?: string }
  /** Remove ONE arm from a branch (`if`/`else if`/`else`) or a `catch` from a
   *  `try`/retry — `stepId` keys the enclosing branch / guard / retry node, `arm`
   *  the discriminator ({@link RemovableArm}). Deletes exactly that arm's span
   *  plus the inter-arm whitespace before its keyword, leaving siblings byte-
   *  intact. REFUSES removing the primary `if` arm / the `try` body (that is
   *  delete-the-whole-block, a different op) and an out-of-range index. */
  | { readonly op: "removeArm"; readonly stepId: string; readonly arm: RemovableArm }
  /** Set / replace / remove the `unwind <text>` compensation on a step inside a
   *  `reversible` block. `stepId` keys the FORWARD step (its IR stepId path IS the
   *  reversible item path `reversible:N/item:M`). `text` non-empty → set/replace
   *  the `unwind …` clause (rendered `unwind <text>`, so pass the compensating
   *  ACTION, e.g. `> Inventory.release` — the saga rule: this step's OWN
   *  compensation, not the previous step's); `text===""` REMOVES the clause
   *  entirely (keyword + span + the whitespace joining it to the forward). Adding
   *  to a step with no unwind inserts ` unwind <text>` after the forward step.
   *  Refuses a step that is not a reversible forward item (nowhere to attach). */
  | { readonly op: "setUnwind"; readonly stepId: string; readonly text: string }
  /** Relocate a step/block (`stepId`) to sit before/after a sibling anchor
   *  (`anchorStepId`) — the reorder + reparent-as-sibling primitive for drag.
   *  The moved span is cut and re-inserted, rebased to the anchor's indent (its
   *  inner block structure preserved). Refuses moving a block into its own body
   *  or onto itself. */
  | { readonly op: "moveStep"; readonly stepId: string; readonly anchorStepId: string; readonly position: "before" | "after" }
  /** Reparent a step/block (`stepId`) to the START of a block's body
   *  (`blockStepId`) — the drop-into-block primitive (esp. an empty block). Cut
   *  + re-insert rebased to the block's inner indent. Refuses a non-block target,
   *  moving into self, or onto itself. */
  | { readonly op: "moveStepInto"; readonly stepId: string; readonly blockStepId: string }
  /** Relocate SEVERAL steps/blocks (`stepIds`, in the caller's selection order) to
   *  sit contiguously before/after ONE sibling anchor (`anchorStepId`) — the
   *  multi-select drag primitive (P2 #19). Every span is cut and re-inserted as
   *  ONE ordered block rebased to the anchor's indent, so the whole move is a
   *  single journaled edit (one undo). Unlike a `moveStep` loop, this never
   *  THROWS on a redundant member: an id that is a DESCENDANT of another moved
   *  block (its span within another's — e.g. a step inside a moved `if`), that
   *  equals the anchor, that ENCLOSES the anchor (would move a block onto an
   *  anchor in its own body), or that doesn't resolve is silently DROPPED; the
   *  survivors still move. Returns no edits (a clean no-op) when nothing survives.
   *  Refuses (`no-span`) only when the ANCHOR itself doesn't resolve. */
  | { readonly op: "moveSteps"; readonly stepIds: readonly string[]; readonly anchorStepId: string; readonly position: "before" | "after" }
  /** Flatten a single-body control block (`stepId`) in place: replace the whole
   *  block with its BODY, dedented one level (the wrapper's `construct {` head +
   *  closing `}` are removed). SUPPORTED — the unambiguous single-body cases:
   *  `parallel { … }` (a single implicit branch or `each` fan-out), `each … { … }`
   *  (the loop head is dropped), `reversible { … }` (its forward items are hoisted;
   *  a reversible carrying an inline `unwind` refuses via the parse guard, since a
   *  bare `unwind` outside a saga is illegal), a single-arm `if cond { … }` (the
   *  CONDITION is dropped — the body always runs), and a `try { … }` with no
   *  `catch`. REFUSES (`compound-step`) the lossy/ambiguous ones — an `if` with an
   *  `else`/`else if`, a `select` (cases aren't a flat flow), a `try`/retry with
   *  catches or an `else`, and a multi-branch `parallel` (would lose arms or
   *  collapse concurrency). Everything routes through `guardInsertParse` so a
   *  transform that would not re-parse is refused, never landed. */
  | { readonly op: "unwrapBlock"; readonly stepId: string }
  /** Change a control block's (`stepId`) construct keyword to `to`, where a
   *  byte-exact + parse-clean transform exists. SUPPORTED: `parallel` ↔ `each`
   *  (a single-body parallel only — the loop head is a `each item in items`
   *  placeholder when morphing parallel→each, and is dropped when each→parallel).
   *  REFUSES (`bad-morph`) every other pair — notably `if` ↔ `select`, whose arm/
   *  case body shapes differ (a select case body must carry a `label:`; an `if`
   *  arm's flat flow does not map cleanly, and the reverse would produce illegal
   *  select bodies) — so no lossy transform can land. */
  | { readonly op: "morphBlock"; readonly stepId: string; readonly to: WrapConstruct }
  /** Set a `parallel`'s merge behaviour + threshold in one op (`stepId` keys the
   *  parallel). `policy` = `join` (proceed at threshold, KEEP losers) / `race`
   *  (proceed, CANCEL losers) / undefined (bare `parallel` — the AND-join, no
   *  keyword); `out` toggles the detach marker (stacks with policy); `count` sets
   *  `join N` / `race N` (omit / undefined = the bare form: `join` = all, `race` =
   *  first-wins). Rewrites ONLY the written merge tokens (`merge.source`), or
   *  inserts right after `parallel` when the clause is absent — every other byte
   *  (owner prefix, body) untouched. REFUSES a `parallel await …` (`merge-await` —
   *  await already implies race, and `await` + `join`/`race` is a parse error) and
   *  a count over a BOOLEAN (named-branch) threshold (`merge-boolean` — replacing
   *  `join (a and b)` with `join N` would silently drop the named merge; edit the
   *  names in text). A bare `parallel` with `policy` undefined + `out` false + no
   *  count clears the clause entirely. */
  | {
      readonly op: "setMergeClause";
      readonly stepId: string;
      readonly policy?: "join" | "race";
      readonly out?: boolean;
      readonly count?: number;
    }
  /** Set the `select`/`select one`/`select N` count on a top-level `select`
   *  (`stepId`). `count` = `"all"` (bare `select`, every matching case) / `"one"`
   *  (first matching) / a positive integer N (first N matching). Rewrites ONLY the
   *  count slot between the `select` keyword and the head/body — the head, cases,
   *  and owner stay byte-intact. Refuses a non-select node and a non-positive N. */
  | { readonly op: "setSelectCount"; readonly stepId: string; readonly count: "all" | "one" | number }
  /** Set the count on a `parallel select` form (`stepId` keys the parallel).
   *  Same `count` domain as `setSelectCount`; rewrites the count slot after
   *  `parallel select`. Refuses a parallel whose body is NOT the `select` form. */
  | { readonly op: "setParallelSelectCount"; readonly stepId: string; readonly count: "all" | "one" | number }
  /** Inline-edit the awaited head/target text — the standalone `await <head>` step
   *  OR a `parallel await <head>` boundary clause (`stepId` keys either node).
   *  Replaces ONLY the head token span (mirror of `setSelectHead`); every other
   *  byte untouched. Refuses a node that is neither an await step nor a parallel
   *  carrying an await clause. */
  | { readonly op: "setAwaitTarget"; readonly stepId: string; readonly text: string }
  /** Set / replace / remove ONE trailing modifier on a CALL step (`stepId` keys the
   *  CallNode). `kind` selects the modifier: `"as"` — the `as <label>` pin alias
   *  (PROCESS.md §7); `"comment"` — the trailing freehand `"comment"` (§1). Order on
   *  the wire is `> Callee(args) "comment" as label`, so each modifier lands in its
   *  canonical slot regardless of the other's presence:
   *    - editing an existing modifier rewrites ONLY its own token span
   *      (`NamedRef.source` / `Verbatim.source`);
   *    - `text===""` REMOVES it, plus the single space joining it to the token
   *      before it (`> Callee "note" as x` → `> Callee as x`, never a double space);
   *    - adding an absent modifier inserts it at its canonical slot — a `comment`
   *      before any `as` (else at the node end), an `as` after any `comment` (else at
   *      the node end).
   *  `as` labels must be a bare identifier (`checkpoint`) — a non-identifier refuses
   *  (`bad-modifier`); a `comment` is any text (rendered as a `"…"` literal). Refuses
   *  a non-call node (`no-modifier-host` — only calls carry these slots; a `do`/`go`
   *  comment or await `as` has its own op path). Routed through `guardInsertParse`. */
  | { readonly op: "setStepModifier"; readonly stepId: string; readonly kind: "as" | "comment"; readonly text: string };

/** The string form step ops are keyed by: the StepId's positional structural
 *  address (`path.join("/")` — the walker `NodeId` minus its process prefix). */
export function stepIdKey(stepId: { readonly path: readonly string[] }): string {
  return stepId.path.join("/");
}

/**
 * Step ops over the process IR, keyed by {@link stepIdKey} (W3, plan §3.5).
 * Spans come off the IR nodes (`NodeBase.source`, lossless over surface
 * syntax); removal is separator-aware, insertion lands on its own line after
 * the anchor statement (block spans include their `}`).
 */
/** Prefix `indent` to every non-blank line of a (possibly multi-line) scaffold,
 *  so a multi-line control-block scaffold authored at relative column 0 nests
 *  correctly under the anchor's indent. Single-line text just gets the prefix
 *  (unchanged from the old behaviour); blank lines stay blank (no trailing ws). */
function indentScaffold(text: string, indent: string): string {
  return text
    .split("\n")
    .map((l) => (l.length > 0 ? indent + l : l))
    .join("\n");
}

/** Belt-and-suspenders for the insert ops (plan §127, "the editor cannot author
 *  a parse error"): apply the candidate edits and refuse if they introduce a NEW
 *  parse error — e.g. a bare `A > B` dropped into a `select` body (which holds
 *  only `case:` labels), which no per-surface palette filter fully rules out.
 *  Compares error counts, so a file with a pre-existing error elsewhere still
 *  edits. This is the funnel-level guarantee; the palette still offers only the
 *  legal moves so the refusal is a rare last resort, not the primary UX. */
function guardInsertParse(src: string, edits: TextEdit[], op: string): TextEdit[] {
  const before = parse(src, "mutation-check.arch").errors.length;
  const after = parse(applyTextEdits(src, edits), "mutation-check.arch").errors.length;
  if (after > before) {
    refuse("would-not-parse", `that ${op === "insertStepInto" ? "insert" : "step"} isn't valid at this position`);
  }
  return edits;
}

/** `insertStepBefore` on the HEAD step of a one-liner `select` case (`hi: > B`)
 *  can't prepend onto the head's own line — the head shares its line with the
 *  `label:`, and a bare step above it would land directly in the select body
 *  (only `case:` labels are legal there → `would-not-parse`). This promotes the
 *  case from `label: s1; s2` to a braced body carrying the new step first:
 *
 *      hi: > B          →      hi {
 *                                  <text>
 *                                  > B
 *                              }
 *
 *  by REPLACING the whole `case.source` span. The `label:` is preserved (as the
 *  brace head), every existing body step is re-emitted on its own line at the
 *  case's inner indent (byte-preserving each step's source), and `text` leads.
 *  Returns undefined when `node` is NOT a one-liner case head — an if/else arm
 *  head, an already-braced case head, or any flat-flow step owns its line and
 *  takes the plain prepend path. */
function oneLinerCaseHeadInsertBefore(
  src: string,
  pm: ProcessModel,
  node: ProcessIrNode,
  text: string,
): TextEdit[] | undefined {
  let hit: { readonly select: Extract<ProcessIrNode, { kind: "select" }>; readonly caseIndex: number } | undefined;
  forEachNode(pm.body, (n) => {
    if (n.kind !== "select") return;
    n.cases.forEach((c, i) => {
      if (c.body && c.body[0] === node) hit = { select: n, caseIndex: i };
    });
  });
  if (!hit) return undefined;
  const c = hit.select.cases[hit.caseIndex]!;
  const caseSpan = c.source;
  const labelSpan = c.label.source;
  const head = c.body?.[0];
  if (!caseSpan || !labelSpan || !head?.source) return undefined;
  // A braced case has `{` between the label and the head; a one-liner has `:`.
  // Only the one-liner needs promotion — the braced head owns its line already.
  const gap = src.slice(labelSpan.end.offset, head.source.start.offset);
  if (gap.includes("{")) return undefined;

  const caseIndent = leadingIndent(src, caseSpan.start.offset);
  const innerIndent = caseIndent + INDENT;
  const label = src.slice(labelSpan.start.offset, labelSpan.end.offset);
  // Re-emit each existing body step at the inner indent, byte-preserving its
  // own source (rebased for any multi-line block it may carry).
  const bodyLines = (c.body ?? []).map((s) => {
    const ssrc = s.source!;
    const raw = src.slice(ssrc.start.offset, ssrc.end.offset);
    return rebaseIndent(raw, leadingIndent(src, ssrc.start.offset), innerIndent);
  });
  const lines = [indentScaffold(text, innerIndent), ...bodyLines];
  const braced = `${label} {\n${lines.join("\n")}\n${caseIndent}}`;
  return [{ start: caseSpan.start.offset, end: caseSpan.end.offset, newText: braced }];
}

export function synthesizeProcessStepMutation(
  src: string,
  pm: ProcessModel,
  m: ProcessStepMutation,
): TextEdit[] {
  // Hop-scoped reassign is keyed by a HOP stepId (`…/hop:H`), which never
  // matches a NODE stepId — resolve it against call hops before the node lookup.
  if (m.op === "reassignStepOwner") {
    let callerSource: SourceSpan | undefined;
    let isHead = false;
    let matched = false;
    forEachCallHop(pm.body, (h, n) => {
      if (stepIdKey(h.stepId) !== m.stepId) return;
      matched = true;
      isHead = n.hops[0] === h;
      callerSource = h.callerSource;
    });
    if (!matched) refuse("no-span", `no step '${m.stepId}' in process '${pm.name || "(anonymous)"}'`);
    // A later chain hop's caller is the previous hop's callee token — rewriting
    // it would corrupt the prior hop. Only the chain head is rewritable.
    if (!isHead) refuse("compound-step", `step '${m.stepId}' is a chained hop — only the chain head reassigns`);
    if (!callerSource) refuse("no-span", `step '${m.stepId}' has an implicit owner — no source to rewrite`);
    // Parse-guarded: the owner ref is spliced verbatim (dotted refs are legal
    // here, so no single-word check applies) — a keyword-named ref ('view',
    // a name the decl grammar itself allows) would corrupt the file (r39).
    return guardInsertParse(
      src,
      [{ start: callerSource.start.offset, end: callerSource.end.offset, newText: m.owner }],
      m.op,
    );
  }

  // setUnwind keys the FORWARD step (its stepId path IS the reversible item path);
  // the unwind data lives on the enclosing reversible's ITEM, not the forward node
  // itself — so resolve it against the reversible items before the node lookup.
  if (m.op === "setUnwind") {
    return setUnwindEdits(src, pm, m);
  }

  // moveSteps keys MULTIPLE nodes — it resolves + relocates its own set (with
  // descendant/self/enclose drops), so it bypasses the single-node lookup below.
  if (m.op === "moveSteps") {
    return moveStepsEdits(src, pm, m);
  }

  const stepKey = m.op === "insertStepInto" ? m.blockStepId : m.stepId;
  let node: ProcessIrNode | undefined;
  forEachNode(pm.body, (n) => {
    if (stepIdKey(n.stepId) === stepKey) node = n;
  });
  if (!node) refuse("no-span", `no step '${stepKey}' in process '${pm.name || "(anonymous)"}'`);
  const span = node.source;
  if (!span) refuse("no-span", `step '${stepKey}' is synthesized — no source to edit`);

  switch (m.op) {
    case "removeStep":
      // A following `|` / `\` line is written against THIS step. Deleting the
      // step would silently re-anchor it — to a different caller, or to nothing
      // — so the glyph is materialized to the caller it resolves to today, in
      // the same edit set (§4.13.4).
      return [
        ...materializeDependentGlyph(pm, stepKey),
        ...removeMemberEdits(src, span.start.offset, span.end.offset),
      ];
    case "replaceStep": {
      if (node.kind !== "call") {
        refuse("compound-step", `step '${m.stepId}' is a ${node.kind} block — only simple calls rewrite`);
      }
      return [{ start: span.start.offset, end: span.end.offset, newText: `${m.from} > ${m.to}` }];
    }
    case "insertStepAfter": {
      // Anchor at the end of the node's LAST line (a block span includes its
      // closing `}`); fall back to the span end when the file ends there.
      const nl = src.indexOf("\n", span.end.offset);
      const at = nl === -1 ? span.end.offset : nl;
      const indent = leadingIndent(src, span.start.offset);
      const line = m.text ?? `${m.from} > ${m.to}`;
      // The step lands BETWEEN this anchor and any `|` / `\` written against
      // it, which would silently hand the glyph to the new step. Pin it first,
      // exactly as a removal does (§4.13.4).
      return guardInsertParse(
        src,
        [
          ...materializeDependentGlyph(pm, stepKey),
          { start: at, end: at, newText: `\n${indentScaffold(line, indent)}` },
        ],
        m.op
      );
    }
    case "insertStepBefore": {
      // A one-liner `select` case head (`hi: > B`) has no line of its own to
      // prepend onto — a bare call above it would sit directly in the select
      // body (only `label:` cases are legal there) and refuse. Promote the case
      // to a braced body with the new step as its first line instead. Every
      // OTHER anchor (a flat-flow step, an `if`/`else` arm head, an already-
      // braced case head) already owns its line — the plain prepend below works.
      // The new line lands directly above THIS step, so if this step is itself
      // a `|` / `\` it would re-anchor onto the newcomer. Pin it first.
      const pin = materializeOwnGlyph(node);
      const promote = oneLinerCaseHeadInsertBefore(src, pm, node, m.text);
      if (promote) return guardInsertParse(src, [...pin, ...promote], m.op);
      // Prepend on the anchor's own line, above it.
      const lineStart = src.lastIndexOf("\n", span.start.offset - 1) + 1;
      const indent = leadingIndent(src, span.start.offset);
      return guardInsertParse(
        src,
        [...pin, { start: lineStart, end: lineStart, newText: `${indentScaffold(m.text, indent)}\n` }],
        m.op
      );
    }
    case "insertStepInto": {
      // Prepend inside the block's braces (for an empty body with no anchor).
      const open = src.indexOf("{", span.start.offset);
      if (open === -1) refuse("compound-step", `step '${m.blockStepId}' is not a block — nothing to insert into`);
      const innerIndent = leadingIndent(src, span.start.offset) + INDENT;
      // The line becomes the block's FIRST statement, directly above whatever
      // was first — so if that was a `|` / `\`, it re-anchors onto the newcomer.
      return guardInsertParse(
        src,
        [
          ...materializeOwnGlyph(firstChildOf(node)),
          { start: open + 1, end: open + 1, newText: `\n${indentScaffold(m.text, innerIndent)}` },
        ],
        m.op
      );
    }
    case "wrapStep":
      return wrapStepEdits(src, pm, node, m);
    case "setTerminatorText": {
      if (node.kind !== "terminator") {
        refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a terminator`);
      }
      const rsrc = node.reason?.source;
      if (!rsrc) refuse("no-span", `terminator '${m.stepId}' has no reason/label text to edit`);
      // Replace just the quoted-string span. Re-escape for a `"…"` literal the
      // same way process-print's `q()` does (the lexer un-escapes only `\"` and
      // `\\`), NOT JSON.stringify (which would over-escape newlines/tabs).
      return [{ start: rsrc.start.offset, end: rsrc.end.offset, newText: `"${m.text.replace(/[\\"]/g, "\\$&")}"` }];
    }
    case "setBranchCondition": {
      if (node.kind !== "branch") {
        refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a branch`);
      }
      const arm = node.arms[m.armIndex];
      if (!arm) refuse("no-span", `branch '${m.stepId}' has no arm ${m.armIndex}`);
      const csrc = arm.condition?.source;
      if (!csrc) refuse("no-span", `arm ${m.armIndex} of '${m.stepId}' has no condition to edit`);
      return [{ start: csrc.start.offset, end: csrc.end.offset, newText: conditionLiteral(m.text) }];
    }
    case "setSelectHead": {
      if (node.kind !== "select") {
        refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a select`);
      }
      const hsrc = node.head?.source;
      if (!hsrc) refuse("no-span", `select '${m.stepId}' has no head to edit`);
      return [{ start: hsrc.start.offset, end: hsrc.end.offset, newText: conditionLiteral(m.text) }];
    }
    case "setCaseLabel": {
      if (node.kind !== "select") {
        refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a select`);
      }
      const c = node.cases[m.caseIndex];
      if (!c) refuse("no-span", `select '${m.stepId}' has no case ${m.caseIndex}`);
      const lsrc = c.label.source;
      if (!lsrc) refuse("no-span", `case ${m.caseIndex} of '${m.stepId}' has no label span`);
      return [{ start: lsrc.start.offset, end: lsrc.end.offset, newText: conditionLiteral(m.text) }];
    }
    case "addElse": {
      if (node.kind !== "branch") {
        refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not an if`);
      }
      if (node.elseArm) refuse("already-present", `if '${m.stepId}' already has an else arm`);
      // Append ` else {\n<indent>}` right after the if's final `}` (span end),
      // so it reads `} else {`; the empty arm takes steps via the insert "+".
      const indent = leadingIndent(src, span.start.offset);
      return guardInsertParse(src, [{ start: span.end.offset, end: span.end.offset, newText: ` else {\n${indent}}` }], m.op);
    }
    case "addCatch": {
      if (node.kind !== "guard" && node.kind !== "retry") {
        refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a try`);
      }
      const indent = leadingIndent(src, span.start.offset);
      // Bare-emit a valid (dotted) error type, else a catch-all `catch { }`.
      const label = m.errorType && /^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/.test(m.errorType) ? ` ${m.errorType}` : "";
      return guardInsertParse(src, [{ start: span.end.offset, end: span.end.offset, newText: ` catch${label} {\n${indent}}` }], m.op);
    }
    case "removeArm":
      return removeArmEdits(src, node, m);
    case "moveStep":
    case "moveStepInto":
      return moveStepEdits(src, pm, node, span, m);
    case "unwrapBlock":
      return unwrapBlockEdits(src, node, span, m);
    case "morphBlock":
      return morphBlockEdits(src, node, span, m);
    case "setMergeClause":
      return setMergeClauseEdits(src, node, span, m);
    case "setSelectCount":
      return setSelectCountEdits(src, node, span, m);
    case "setParallelSelectCount":
      return setParallelSelectCountEdits(src, node, span, m);
    case "setAwaitTarget":
      return setAwaitTargetEdits(src, node, m);
    case "setStepModifier":
      return setStepModifierEdits(src, node, span, m);
  }
}

/** Render the merge tokens for a `setMergeClause` op: `out` (when detached) then
 *  `join`/`race` (when a policy) then the count digit (when set). Joined by single
 *  spaces; empty when the clause is fully cleared. Matches `process-print`'s
 *  `mergeText` byte-for-byte (minus the leading space it prepends). */
function renderMergeTokens(policy: "join" | "race" | undefined, out: boolean, count: number | undefined): string {
  const parts: string[] = [];
  if (out) parts.push("out");
  if (policy) {
    parts.push(policy);
    if (count !== undefined) parts.push(String(count));
  }
  return parts.join(" ");
}

/** Set a parallel's merge behaviour — the `setMergeClause` synthesizer. Rewrites
 *  the written merge tokens (`merge.source`), inserts them after `parallel` when
 *  the clause is absent, or deletes the clause when fully cleared. Refuses an
 *  await-boundary parallel and a count over a boolean (named) threshold. */
function setMergeClauseEdits(
  src: string,
  node: ProcessIrNode,
  span: SourceSpan,
  m: { readonly op: "setMergeClause"; readonly stepId: string; readonly policy?: "join" | "race"; readonly out?: boolean; readonly count?: number },
): TextEdit[] {
  if (node.kind !== "parallel") {
    refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a parallel`);
  }
  if (node.awaitClause) {
    refuse("merge-await", `cannot set a join/race merge on a 'parallel await …' — await already implies race`);
  }
  const count = m.count;
  if (count !== undefined && (!Number.isInteger(count) || count < 1)) {
    refuse("bad-count", `merge count must be a positive integer, got ${count}`);
  }
  // A count only means anything under a policy, and only over a COUNT threshold.
  // Setting one over a boolean (named-branch) threshold would drop the names.
  if (count !== undefined && node.merge.threshold?.kind === "boolean") {
    refuse("merge-boolean", `cannot set a numeric count over a named-branch merge ('${node.merge.threshold.text?.text ?? "…"}') — edit the branch names in text`);
  }
  const out = m.out ?? node.merge.detached;
  const tokens = renderMergeTokens(m.policy, out, count);
  const existing = node.merge.source;
  if (existing) {
    if (tokens === "") {
      // Clear the clause: delete the merge tokens plus the single space joining
      // them to `parallel` (`parallel out {` → `parallel {`).
      const start = trimBlankBackward(src, existing.start.offset);
      return guardInsertParse(src, [{ start, end: existing.end.offset, newText: tokens }], m.op);
    }
    return guardInsertParse(src, [{ start: existing.start.offset, end: existing.end.offset, newText: tokens }], m.op);
  }
  // No clause yet — insert ` <tokens>` right after the `parallel` keyword.
  if (tokens === "") return []; // clearing an already-bare parallel: no-op.
  const kw = src.indexOf("parallel", span.start.offset);
  if (kw === -1 || kw >= span.end.offset) refuse("no-span", `parallel '${m.stepId}' has no 'parallel' keyword to anchor a merge clause`);
  const at = kw + "parallel".length;
  return guardInsertParse(src, [{ start: at, end: at, newText: ` ${tokens}` }], m.op);
}

/** Render a select-count keyword: `""` for all (bare), `one`, or the digit(s). */
function renderSelectCount(count: "all" | "one" | number): string {
  if (count === "all") return "";
  if (count === "one") return "one";
  return String(count);
}

/** Rewrite the count slot of a `select` (`[owner] select [count] [head] {`).
 *  Locates the `select` keyword textually in the node head (it cannot appear as
 *  an identifier there), then replaces the region up to the head or `{` with the
 *  new count keyword. Shared by top-level select and `parallel select`. */
function rewriteSelectCountSlot(
  src: string,
  span: SourceSpan,
  headStart: number | undefined,
  count: "all" | "one" | number,
  op: string,
): TextEdit[] {
  if (count !== "all" && count !== "one" && (!Number.isInteger(count) || count < 1)) {
    refuse("bad-count", `select count must be a positive integer, got ${count}`);
  }
  const kw = src.indexOf("select", span.start.offset);
  if (kw === -1 || kw >= span.end.offset) refuse("no-span", `select has no 'select' keyword to anchor a count`);
  const slotStart = kw + "select".length; // just past `select`
  // The slot ends at the head token (if any) or the opening `{`; either way it is
  // the run of `[ ][count]` between `select` and that boundary.
  const open = src.indexOf("{", slotStart);
  const boundary = headStart !== undefined ? headStart : open;
  if (boundary === -1 || boundary < slotStart) refuse("no-span", `select count slot is malformed`);
  const kwText = renderSelectCount(count);
  // Re-emit exactly one separating space before a non-empty keyword; for `all`
  // (empty) collapse to a single space so `select {`/`select "head"` stays valid.
  const newText = kwText === "" ? " " : ` ${kwText} `;
  return guardInsertParse(src, [{ start: slotStart, end: boundary, newText }], op);
}

/** Set a top-level `select`'s count — the `setSelectCount` synthesizer. */
function setSelectCountEdits(
  src: string,
  node: ProcessIrNode,
  span: SourceSpan,
  m: { readonly op: "setSelectCount"; readonly stepId: string; readonly count: "all" | "one" | number },
): TextEdit[] {
  if (node.kind !== "select") {
    refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a select`);
  }
  return rewriteSelectCountSlot(src, span, node.head?.source?.start.offset, m.count, m.op);
}

/** Set a `parallel select`'s count — the `setParallelSelectCount` synthesizer. */
function setParallelSelectCountEdits(
  src: string,
  node: ProcessIrNode,
  span: SourceSpan,
  m: { readonly op: "setParallelSelectCount"; readonly stepId: string; readonly count: "all" | "one" | number },
): TextEdit[] {
  if (node.kind !== "parallel" || node.body.form !== "select") {
    refuse("compound-step", `step '${m.stepId}' is not a 'parallel select' form`);
  }
  // The `parallel select` form has no head — the count slot runs to the `{`.
  return rewriteSelectCountSlot(src, span, undefined, m.count, m.op);
}

/** Inline-edit an await head/target — the `setAwaitTarget` synthesizer. Handles
 *  the standalone `await <head>` step and the `parallel await <head>` clause. */
function setAwaitTargetEdits(
  src: string,
  node: ProcessIrNode,
  m: { readonly op: "setAwaitTarget"; readonly stepId: string; readonly text: string },
): TextEdit[] {
  let tsrc: SourceSpan | undefined;
  if (node.kind === "await") tsrc = node.target.source;
  else if (node.kind === "parallel" && node.awaitClause) tsrc = node.awaitClause.head.source;
  else refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not an await`);
  if (!tsrc) refuse("no-span", `await '${m.stepId}' has no head/target span to edit`);
  return guardInsertParse(src, [{ start: tsrc.start.offset, end: tsrc.end.offset, newText: awaitTargetLiteral(m.text) }], m.op);
}

/** Emit a `"…"` comment literal — the exact inverse of the lexer's un-escape
 *  (`\"`→`"`, `\\`→`\`), matching `process-print`'s `q()` byte-for-byte. NOT
 *  `JSON.stringify` (which would over-escape newlines/tabs — a comment may span
 *  lines and reads back the same). */
function commentLiteral(text: string): string {
  return `"${text.replace(/[\\"]/g, "\\$&")}"`;
}

/** Find the `as` keyword offset preceding an `as <label>` identifier whose span
 *  is `labelSrc` (the parser records only the identifier's span). Walks back over
 *  the inter-token whitespace, then over the literal `as`. Returns the offset of
 *  the `a` in `as` — the point a `comment` inserts BEFORE (`hops "c" as x`). */
function asKeywordStart(src: string, labelSrc: SourceSpan): number {
  const afterKw = trimBlankBackward(src, labelSrc.start.offset); // just past `as`
  return afterKw - "as".length; // the `a` of `as`
}

/** Set / replace / remove one trailing modifier (`as` / `comment`) on a call —
 *  the `setStepModifier` synthesizer. Each modifier lands in its canonical wire
 *  slot (`> Callee(args) "comment" as label`, PROCESS.md §1 / §7) regardless of
 *  the other's presence; editing rewrites only its own token span, removal eats
 *  the joining space, an add inserts at the slot. Refuses a non-call node and a
 *  non-identifier `as` label. */
function setStepModifierEdits(
  src: string,
  node: ProcessIrNode,
  span: SourceSpan,
  m: { readonly op: "setStepModifier"; readonly stepId: string; readonly kind: "as" | "comment"; readonly text: string },
): TextEdit[] {
  if (node.kind !== "call") {
    refuse("no-modifier-host", `step '${m.stepId}' is a ${node.kind}, not a call — it carries no '${m.kind}' modifier`);
  }
  const text = m.text.trim();

  if (m.kind === "as") {
    // Real-lexer check (one IDENTIFIER token): the old ASCII regex wrongly
    // refused legal unicode labels and admitted keywords (r46). Keyword
    // corruption was caught downstream by guardInsertParse; unicode refusal
    // was a silent capability gap.
    if (text !== "") {
      const { tokens, errors } = tokenize(text);
      const ok =
        errors.length === 0 &&
        tokens.length === 2 &&
        tokens[1]!.type === "EOF" &&
        tokens[0]!.type === "IDENTIFIER";
      if (!ok) refuse("bad-modifier", `an 'as' label must be a bare identifier, got '${m.text}'`);
    }
    const asSrc = node.asName?.source;
    if (asSrc) {
      if (text === "") {
        // Remove ` as label` entirely — the keyword, its label, and the space
        // joining it to the token before (comment or hops).
        const kw = asKeywordStart(src, asSrc);
        const start = trimBlankBackward(src, kw);
        return guardInsertParse(src, [{ start, end: asSrc.end.offset, newText: "" }], m.op);
      }
      // Replace just the label identifier token.
      return guardInsertParse(src, [{ start: asSrc.start.offset, end: asSrc.end.offset, newText: text }], m.op);
    }
    if (text === "") return []; // remove-when-absent: no-op.
    // Add `as` — always last in the emit order, so append at the node span end
    // (which the parser extended past any comment).
    return guardInsertParse(src, [{ start: span.end.offset, end: span.end.offset, newText: ` as ${text}` }], m.op);
  }

  // kind === "comment"
  const cSrc = node.comment?.source;
  if (cSrc) {
    if (text === "") {
      // Remove the `"comment"` literal plus the space joining it to the hops.
      const start = trimBlankBackward(src, cSrc.start.offset);
      return guardInsertParse(src, [{ start, end: cSrc.end.offset, newText: "" }], m.op);
    }
    return guardInsertParse(src, [{ start: cSrc.start.offset, end: cSrc.end.offset, newText: commentLiteral(text) }], m.op);
  }
  if (text === "") return []; // remove-when-absent: no-op.
  // Add a comment — it goes BEFORE any `as`. Insert at the `as` keyword when one
  // exists (the pre-`as` space already there separates it), else append at the
  // node end (after the hops/args).
  const asSrc = node.asName?.source;
  if (asSrc) {
    const at = asKeywordStart(src, asSrc);
    return guardInsertParse(src, [{ start: at, end: at, newText: `${commentLiteral(text)} ` }], m.op);
  }
  return guardInsertParse(src, [{ start: span.end.offset, end: span.end.offset, newText: ` ${commentLiteral(text)}` }], m.op);
}

/** The inner `{ … }` body of a block node, as byte offsets and text. `open` is
 *  the FIRST `{` in the node's source (the block header's brace); `close` is the
 *  node's final `}` (the span ends there). Refuses a node with no braced body
 *  (a one-liner `each: …` / an ownerless construct). Mirrors how `wrapStepEdits`
 *  works purely off the source bytes, so it is construct-agnostic and lossless
 *  over comments / nested blocks. */
function blockBody(
  src: string,
  span: SourceSpan,
  what: string,
): { open: number; close: number; inner: string } {
  const open = src.indexOf("{", span.start.offset);
  const close = src.lastIndexOf("}", span.end.offset - 1);
  if (open === -1 || close === -1 || close <= open || open >= span.end.offset) {
    refuse("compound-step", `${what} has no braced body to unwrap`);
  }
  return { open, close, inner: src.slice(open + 1, close) };
}

/** Dedent a block's inner body by one level, rebased to the block's own indent,
 *  so the hoisted statements sit where the wrapper did. The result replaces the
 *  block span, which starts AFTER the wrapper's leading indent (`node.source`
 *  begins at the construct keyword) — so, exactly like {@link rebaseIndent}, the
 *  FIRST emitted line carries no leading indent (the existing source indent
 *  before the block covers it) and every later line carries `blockIndent`.
 *
 *  The inner text (`src[open+1 .. close]`) starts right after `{` (its first line
 *  is empty / trailing ws) and ends just before the closing `}`'s own indent; we
 *  drop those framing lines, strip one level (`blockIndent + INDENT`) off each
 *  remaining line, and re-prefix per the line-0 convention. */
function dedentBody(inner: string, blockIndent: string): string {
  const lines = inner.split("\n");
  if (lines.length > 0 && lines[0]!.trim() === "") lines.shift();
  if (lines.length > 0 && lines[lines.length - 1]!.trim() === "") lines.pop();
  const strip = blockIndent + INDENT;
  const dedent = (l: string): string => {
    if (l.startsWith(strip)) return l.slice(strip.length);
    // A shallower line (shouldn't happen for well-formed nesting) — strip only
    // the wrapper's inner INDENT if present, else its leading whitespace.
    if (l.startsWith(INDENT)) return l.slice(INDENT.length);
    return l.replace(/^[ \t]+/, "");
  };
  return lines
    .map((l, i) => {
      if (l.length === 0) return l;
      const body = dedent(l);
      return i === 0 ? body : blockIndent + body;
    })
    .join("\n");
}

/** Refuse a node whose typed arm structure makes flattening lossy/ambiguous —
 *  the guard that keeps `unwrapBlock` to the single-body cases. */
function refuseAmbiguousUnwrap(node: ProcessIrNode): void {
  switch (node.kind) {
    case "branch":
      if (node.elseArm || node.arms.length > 1) {
        refuse("compound-step", `cannot unwrap an if with else / else-if arms — it would lose branches`);
      }
      return;
    case "select":
      refuse("compound-step", `cannot unwrap a select — its cases are not a flat flow`);
      return; // unreachable
    case "guard":
      if (node.catches.length > 0) {
        refuse("compound-step", `cannot unwrap a try with catch clauses — they would be lost`);
      }
      return;
    case "retry":
      refuse("compound-step", `cannot unwrap a retry loop — its catch/else handlers would be lost`);
      return; // unreachable
    case "parallel":
      if (node.awaitClause) {
        refuse("compound-step", `cannot unwrap a parallel await — the timeout branch would be lost`);
      }
      if (node.body.form === "branches" || node.body.form === "select") {
        const branches = node.body.branches;
        // A single IMPLICIT branch is a plain body wrapper — safe to flatten.
        // Any explicit / multiple branch(es) carry concurrency + merge meaning.
        if (!(branches.length === 1 && branches[0]!.implicit)) {
          refuse("compound-step", `cannot unwrap a multi-branch parallel — it would collapse concurrent branches`);
        }
      }
      return;
    case "owner_block":
      // An owner block is not offered for unwrap (it has no construct keyword to
      // remove) — refuse cleanly rather than emit a bare-braces flatten.
      refuse("compound-step", `an owner block has no construct wrapper to unwrap`);
      return; // unreachable
    default:
      return;
  }
}

/** Flatten a single-body block in place — the `unwrapBlock` synthesizer. */
function unwrapBlockEdits(
  src: string,
  node: ProcessIrNode,
  span: SourceSpan,
  m: { readonly op: "unwrapBlock"; readonly stepId: string },
): TextEdit[] {
  const UNWRAPPABLE = new Set(["parallel", "loop", "reversible", "branch", "guard"]);
  if (!UNWRAPPABLE.has(node.kind)) {
    refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not an unwrappable block`);
  }
  refuseAmbiguousUnwrap(node);
  const { inner } = blockBody(src, span, `step '${m.stepId}'`);
  const blockIndent = leadingIndent(src, span.start.offset);
  const body = dedentBody(inner, blockIndent);
  // Replace the whole block span (head + body + `}`) with just the dedented body.
  return guardInsertParse(src, [{ start: span.start.offset, end: span.end.offset, newText: body }], m.op);
}

/** Change a block's construct keyword — the `morphBlock` synthesizer. Only the
 *  `parallel` ↔ `each` pair has a byte-clean transform; everything else refuses
 *  (`bad-morph`). The head region (from the node start up to the opening `{`) is
 *  rewritten; the body bytes are untouched. */
function morphBlockEdits(
  src: string,
  node: ProcessIrNode,
  span: SourceSpan,
  m: { readonly op: "morphBlock"; readonly stepId: string; readonly to: WrapConstruct },
): TextEdit[] {
  const open = src.indexOf("{", span.start.offset);
  if (open === -1 || open >= span.end.offset) {
    refuse("bad-morph", `step '${m.stepId}' is not a braced block — nothing to morph`);
  }
  // A leading owner prefix (`A parallel {`) must survive the keyword swap; keep
  // everything before the construct keyword. We locate the keyword token in the
  // head region rather than assuming it starts the span.
  const head = src.slice(span.start.offset, open); // e.g. "A parallel " / "each item in xs "
  if (node.kind === "parallel" && m.to === "each") {
    // Only a SINGLE-body parallel maps onto a loop (one body). Reuse the same
    // structural guard as unwrap's parallel case.
    if (node.awaitClause || node.merge.detached || node.merge.policy || node.merge.threshold) {
      refuse("bad-morph", `cannot morph a parallel with a merge/await clause to each`);
    }
    if (node.body.form === "fanOut") {
      // `parallel each <head> { … }` already carries a valid loop head — just drop
      // the `parallel ` marker, keeping `each <head>` (and any owner prefix).
      const newHead = head.replace(/parallel\s+(?=each\b)/, "");
      return guardInsertParse(src, [{ start: span.start.offset, end: open, newText: newHead }], m.op);
    }
    const branches = node.body.form === "branches" || node.body.form === "select" ? node.body.branches : [];
    if (!(branches.length === 1 && branches[0]!.implicit)) {
      refuse("bad-morph", `cannot morph a multi-branch parallel to each — a loop has a single body`);
    }
    // A plain single-body `parallel { … }` has no loop head — inject a placeholder
    // one. Swap `parallel` → `each item in items`, preserving any owner prefix.
    const newHead = head.replace(/parallel\b/, "each item in items");
    return guardInsertParse(src, [{ start: span.start.offset, end: open, newText: newHead }], m.op);
  }
  if (node.kind === "loop" && m.to === "parallel") {
    // Drop the loop head: everything after the `each` keyword up to `{` becomes
    // `parallel `. Preserve an owner prefix before `each`.
    const newHead = head.replace(/each\b.*$/s, "parallel ");
    return guardInsertParse(src, [{ start: span.start.offset, end: open, newText: newHead }], m.op);
  }
  refuse(
    "bad-morph",
    `no clean morph from ${node.kind} to ${m.to} — arm/case shapes differ (would be lossy)`,
  );
}

/** Scan backward from `at` over spaces/tabs, stopping at the first non-blank char
 *  (typically the preceding arm's closing `}`), and return that offset. Used to
 *  eat the ` ` in `} else {` / `} catch {` so an arm removal leaves the previous
 *  arm's brace byte-intact. Newlines/indent are consumed too (a `} \n    catch`
 *  layout collapses cleanly) but stops AT the preceding non-blank. */
function trimBlankBackward(src: string, at: number): number {
  let s = at;
  while (s > 0 && (src[s - 1] === " " || src[s - 1] === "\t" || src[s - 1] === "\n" || src[s - 1] === "\r")) s--;
  return s;
}

/** Remove one arm from a branch / guard / retry — the `removeArm` synthesizer.
 *  Deletes the arm's own span plus the inter-arm whitespace before its `else` /
 *  `catch` keyword, leaving every sibling arm byte-intact. Refuses the primary
 *  `if` arm, the `try` body, a kind/node mismatch, and an out-of-range index. */
function removeArmEdits(
  src: string,
  node: ProcessIrNode,
  m: { readonly op: "removeArm"; readonly stepId: string; readonly arm: RemovableArm },
): TextEdit[] {
  let armSource: SourceSpan | undefined;
  switch (m.arm.kind) {
    case "else": {
      if (node.kind !== "branch") refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not an if`);
      if (!node.elseArm) refuse("arm-not-present", `if '${m.stepId}' has no else arm to remove`);
      armSource = node.elseArm.source;
      break;
    }
    case "elseIf": {
      if (node.kind !== "branch") refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not an if`);
      // arm 0 is the primary `if` — removing it is delete-the-whole-block, not an
      // arm drop. Only `else if` arms (index ≥ 1) are removable here.
      if (m.arm.index < 1) refuse("arm-not-present", `cannot remove the primary 'if' arm — delete the whole block instead`);
      const arm = node.arms[m.arm.index];
      if (!arm) refuse("arm-not-present", `if '${m.stepId}' has no else-if arm ${m.arm.index}`);
      armSource = arm.source;
      break;
    }
    case "catch": {
      if (node.kind !== "guard" && node.kind !== "retry") refuse("compound-step", `step '${m.stepId}' is a ${node.kind}, not a try`);
      const c = node.catches[m.arm.index];
      if (!c) refuse("arm-not-present", `try '${m.stepId}' has no catch ${m.arm.index}`);
      armSource = c.source;
      break;
    }
  }
  if (!armSource) refuse("no-span", `arm of '${m.stepId}' carries no source span`);
  const start = trimBlankBackward(src, armSource.start.offset);
  // guardInsertParse is a removal backstop too — it refuses if the result would
  // gain a parse error (it never should here; the arm span is self-contained).
  return guardInsertParse(src, [{ start, end: armSource.end.offset, newText: "" }], m.op);
}

/** Set / replace / remove the `unwind …` on a reversible forward item — the
 *  `setUnwind` synthesizer. Resolves the reversible + item off the FORWARD step's
 *  stepId key (its path IS the item path `reversible:N/item:M`). */
function setUnwindEdits(
  src: string,
  pm: ProcessModel,
  m: { readonly op: "setUnwind"; readonly stepId: string; readonly text: string },
): TextEdit[] {
  // Locate the reversible item whose forward step (or the item line itself) keys
  // to m.stepId. The forward node's stepId path equals the item path, so match on
  // either the forward node's key or the synthesized item path key.
  let itemSource: SourceSpan | undefined;
  let unwindSource: SourceSpan | undefined;
  let forwardEnd: number | undefined;
  let found = false;
  forEachNode(pm.body, (n) => {
    if (n.kind !== "reversible") return;
    n.items.forEach((it, i) => {
      const itemKey = [...n.stepId.path, `item:${i}`].join("/");
      const fwdKey = it.forward ? stepIdKey(it.forward.stepId) : undefined;
      if (itemKey !== m.stepId && fwdKey !== m.stepId) return;
      found = true;
      itemSource = it.source;
      unwindSource = it.unwind?.source;
      // The forward action's span end is where a NEW ` unwind …` is appended.
      forwardEnd = it.forward?.source?.end.offset;
    });
  });
  if (!found) refuse("not-reversible-item", `step '${m.stepId}' is not a reversible forward item — no compensation to set`);

  const text = m.text.trim();

  if (unwindSource) {
    if (text === "") {
      // Remove the whole `unwind …` clause plus the whitespace joining it to the
      // forward step (`A > B unwind Y` → `A > B`). A reversal-only item (no
      // forward) would collapse to an empty line — that removes the item cleanly.
      const start = trimBlankBackward(src, unwindSource.start.offset);
      return guardInsertParse(src, [{ start, end: unwindSource.end.offset, newText: "" }], m.op);
    }
    // Replace the action after the `unwind` keyword. Keep the keyword; rewrite the
    // span from just past `unwind` to the clause end.
    const kwEnd = unwindSource.start.offset + "unwind".length;
    return guardInsertParse(src, [{ start: kwEnd, end: unwindSource.end.offset, newText: ` ${text}` }], m.op);
  }

  // No unwind yet.
  if (text === "") return []; // remove-when-absent: nothing to do.
  const at = forwardEnd ?? itemSource?.end.offset;
  if (at === undefined) refuse("no-span", `reversible item '${m.stepId}' has no forward span to append an unwind to`);
  return guardInsertParse(src, [{ start: at, end: at, newText: ` unwind ${text}` }], m.op);
}

/** Emit a free-form condition / select-head / case-label token: a BARE
 *  identifier when the text is one, else a re-escaped `"…"` literal (the lexer
 *  un-escapes only `\"` and `\\`, so re-escape those — NOT `JSON.stringify`,
 *  which over-escapes). Both forms parse back to the same free-form Verbatim. */
function conditionLiteral(text: string): string {
  // Bare only what the lexer accepts as ONE identifier token: `$` is not an
  // identifier char and a reserved keyword lexes as a keyword token — either,
  // emitted bare (`if go {`, `$fast:`), left the file unparseable (r39). The
  // quoted form parses back to the same free-form Verbatim.
  return /^[A-Za-z_]\w*$/.test(text) && !RESERVED_KEYWORDS.has(text)
    ? text
    : `"${text.replace(/[\\"]/g, "\\$&")}"`;
}

/** Emit an `await` head/target token — mirrors `process-print`'s `awaitText`:
 *  free-form words (`1 day`, `until ready`) stay BARE; quote when the text holds a
 *  `"`, is empty, or any word re-lexes as a reserved keyword (`await go now` would
 *  otherwise stop the free-form scan at `go`). The quoted form re-escapes `\`/`"`
 *  the same way `conditionLiteral` does (the lexer un-escapes only those). */
function awaitTargetLiteral(text: string): string {
  const bareSafe = /^[\w][\w\s]*$/.test(text) && !text.split(/\s+/).some((w) => RESERVED_KEYWORDS.has(w));
  return bareSafe ? text : `"${text.replace(/[\\"]/g, "\\$&")}"`;
}

/** Rebase a (possibly multi-line) block to a new base indent: the span starts at
 *  the node text (line 0 has NO leading indent — `node.source` starts after it),
 *  so line 0 just gets `newIndent`; lines 2+ carry `oldIndent + relative`, so we
 *  strip `oldIndent` and re-prefix `newIndent`, preserving inner structure. */
function rebaseIndent(text: string, oldIndent: string, newIndent: string): string {
  return text
    .split("\n")
    .map((l, i) => {
      if (i === 0) return newIndent + l;
      if (l.length === 0) return l;
      const stripped = l.startsWith(oldIndent) ? l.slice(oldIndent.length) : l.replace(/^[ \t]+/, "");
      return newIndent + stripped;
    })
    .join("\n");
}

/** True when `inner` sits fully inside `outer` (used to refuse moving a block
 *  into its own body). */
function spanWithin(inner: SourceSpan, outer: SourceSpan): boolean {
  return inner.start.offset >= outer.start.offset && inner.end.offset <= outer.end.offset;
}

/** Cut a step/block span and re-insert it at a new location (rebased to the
 *  destination indent) — the shared body of `moveStep` / `moveStepInto`. Emits
 *  the separator-aware removal PLUS a zero-width insertion; the two never overlap
 *  because the destination anchor is guarded to sit outside the moved span. */
function moveStepEdits(
  src: string,
  pm: ProcessModel,
  node: ProcessIrNode,
  srcSpan: SourceSpan,
  m:
    | { readonly op: "moveStep"; readonly stepId: string; readonly anchorStepId: string; readonly position: "before" | "after" }
    | { readonly op: "moveStepInto"; readonly stepId: string; readonly blockStepId: string },
): TextEdit[] {
  const find = (key: string): ProcessIrNode | undefined => {
    let found: ProcessIrNode | undefined;
    forEachNode(pm.body, (n) => {
      if (stepIdKey(n.stepId) === key) found = n;
    });
    return found;
  };
  const raw = src.slice(srcSpan.start.offset, srcSpan.end.offset);
  const oldIndent = leadingIndent(src, srcSpan.start.offset);

  let insertAt: number;
  let targetIndent: string;
  let lead: boolean; // insert `<text>\n` (before an anchor) vs `\n<text>` (after)

  if (m.op === "moveStep") {
    const anchor = find(m.anchorStepId);
    if (!anchor || !anchor.source) refuse("no-span", `no anchor step '${m.anchorStepId}' in process '${pm.name || "(anonymous)"}'`);
    if (m.anchorStepId === m.stepId) refuse("compound-step", `cannot move a step relative to itself`);
    if (spanWithin(anchor.source, srcSpan)) refuse("range-crosses-block", `cannot move a block onto an anchor inside its own body`);
    targetIndent = leadingIndent(src, anchor.source.start.offset);
    if (m.position === "after") {
      const nl = src.indexOf("\n", anchor.source.end.offset);
      insertAt = nl === -1 ? anchor.source.end.offset : nl;
      lead = false;
    } else {
      insertAt = src.lastIndexOf("\n", anchor.source.start.offset - 1) + 1;
      lead = true;
    }
  } else {
    const block = find(m.blockStepId);
    if (!block || !block.source) refuse("no-span", `no block step '${m.blockStepId}' in process '${pm.name || "(anonymous)"}'`);
    if (m.blockStepId === m.stepId) refuse("compound-step", `cannot move a block into itself`);
    if (spanWithin(block.source, srcSpan)) refuse("range-crosses-block", `cannot move a block into its own body`);
    const open = src.indexOf("{", block.source.start.offset);
    if (open === -1 || open >= block.source.end.offset) refuse("compound-step", `step '${m.blockStepId}' is not a block — nothing to move into`);
    targetIndent = leadingIndent(src, block.source.start.offset) + INDENT;
    insertAt = open + 1;
    lead = false;
  }

  // The destination must not fall inside the span being cut (would corrupt).
  if (insertAt > srcSpan.start.offset && insertAt < srcSpan.end.offset) {
    refuse("range-crosses-block", `move destination lands inside the moved span`);
  }
  // A move is a removal AND an insert, so it carries every glyph hazard both
  // do (§4.13.4). Three things can silently change meaning:
  //   1. what the moved step leaves behind — its dependents re-anchor upward;
  //   2. what it lands next to — it takes the destination anchor's dependent,
  //      or displaces the anchor it is placed above;
  //   3. the moved line ITSELF, if it is a glyph: "the step above me" is a
  //      different step now.
  // (3) is applied to the text being carried, since an edit at the old span
  // would be cut away with it.
  const carried = moveGlyphPin(node, raw, srcSpan);
  const landingPins =
    m.op === "moveStep"
      ? m.position === "after"
        ? materializeDependentGlyph(pm, m.anchorStepId)
        : materializeOwnGlyph(find(m.anchorStepId))
      : // `moveStepInto` prepends inside the block, so it displaces whatever was
        // that block's first statement — the same hazard `insertStepInto` has.
        materializeOwnGlyph(firstChildOf(find(m.blockStepId)));
  const pins = dedupeEdits([...materializeDependentGlyph(pm, m.stepId), ...landingPins]).filter(
    (e) => e.start < srcSpan.start.offset || e.start >= srcSpan.end.offset
  );

  const rebased = rebaseIndent(carried, oldIndent, targetIndent);
  const insertion: TextEdit = lead
    ? { start: insertAt, end: insertAt, newText: `${rebased}\n` }
    : { start: insertAt, end: insertAt, newText: `\n${rebased}` };
  return [...pins, ...removeMemberEdits(src, srcSpan.start.offset, srcSpan.end.offset), insertion];
}

/** Relocate SEVERAL spans to one anchor as ONE ordered, contiguous block — the
 *  multi-select drag primitive (`moveSteps`). Resolves + prunes its own set:
 *  drops an id that is a DESCENDANT of another moved node, one that EQUALS or
 *  ENCLOSES the anchor, a duplicate, or an unresolvable id — never throwing on a
 *  redundant member (only a missing ANCHOR refuses). Every survivor's span is
 *  cut (separator-aware) and re-inserted, in the caller's order, rebased to the
 *  anchor's indent. Returns `[]` (a clean no-op) when nothing survives. */
/**
 * Edits that pin a `|` / `\` line whose anchor is about to be deleted.
 *
 * Both glyphs mean "the step written above me", so removing that step silently
 * re-points the glyph at whatever ends up above it — a valid-looking edge with a
 * different caller, or none at all. Before the removal lands, the dependent line
 * is rewritten to the caller it resolves to RIGHT NOW, turning an implicit
 * reference into the explicit one it already meant.
 *
 * Only the FIRST dependent line PER GLYPH needs it: once it names its caller,
 * the lines after it anchor on that, unchanged. The two glyphs see different
 * things though, so they are scanned separately with the SAME transparency
 * `advanceAnchor` uses (§4.13.4 rule 2):
 *
 *  - `|` reads past prose / `do` / `go`; a note ANSWERS it, and so does any
 *    owned node, so a `|` dependent may be a call, a note, or a control node.
 *  - `\` reads past a note as well, since a note leaves the tail untouched, and
 *    only ever binds to a call (the glyph is call-only).
 *
 * Both can be live at once — a `|` note followed by a `\` line both depend on
 * the step above — so this returns an edit per dependent.
 */
/** Drop edits that repeat a span another already covers. Two pin scans can land
 *  on the same glyph when a moved step sits next to its own destination. */
function dedupeEdits(edits: readonly TextEdit[]): TextEdit[] {
  const seen = new Set<number>();
  const out: TextEdit[] = [];
  for (const e of edits) {
    if (seen.has(e.start)) continue;
    seen.add(e.start);
    out.push(e);
  }
  return out;
}

/** The moved step's own text, with its `|` / `\` resolved in place. An edit at
 *  the old span would be cut away with the move, so the pin is baked into the
 *  carried text instead. */
function moveGlyphPin(node: ProcessIrNode, raw: string, srcSpan: SourceSpan): string {
  const pin = materializeOwnGlyph(node)[0];
  if (!pin) return raw;
  const from = pin.start - srcSpan.start.offset;
  const to = pin.end - srcSpan.start.offset;
  if (from < 0 || to > raw.length) return raw; // not inside the carried text
  return `${raw.slice(0, from)}${pin.newText}${raw.slice(to)}`;
}

/** The first statement inside a block-bearing node — the line an insert-into
 *  lands directly above. `undefined` when the node has no body, or an empty one
 *  (nothing can be re-anchored). */
function firstChildOf(node: ProcessIrNode | undefined): ProcessIrNode | undefined {
  if (!node) return undefined;
  let first: ProcessIrNode | undefined;
  // Walk the node's own flows rather than reaching for a `body` shape: a block
  // may hang off `body`, `arms`, `cases`, `attempt`, … and `forEachFlow` already
  // knows every one of them.
  forEachFlow([node], (flow) => {
    if (flow.length === 1 && flow[0] === node) return; // the node itself
    first ??= flow[0];
  });
  return first;
}

/** Pin ONE node's own `|` / `\` to the caller it resolves to today. Used when a
 *  step is about to be inserted directly ABOVE it: the glyph means "the step
 *  written above me", so the new line would take the anchor's place silently. */
function materializeOwnGlyph(node: ProcessIrNode | undefined): TextEdit[] {
  const owner = node ? ownerOfNode(node) : undefined;
  if (!owner || (owner.form !== "ditto" && owner.form !== "snake")) return [];
  const glyph = owner.source;
  const name = owner.instance ?? owner.resolved?.join(".");
  if (!glyph || !name) return []; // nothing resolved to pin — already a TODO
  return [{ start: glyph.start.offset, end: glyph.end.offset, newText: name }];
}

function materializeDependentGlyph(pm: ProcessModel, removedKey: string): TextEdit[] {
  const dependents: Owner[] = [];
  /** Transparent to BOTH glyphs: contributes neither a head nor a tail. */
  const seeThrough = (n: ProcessIrNode): boolean =>
    n.kind === "prose" || n.kind === "description" || n.kind === "splice" || n.kind === "reentry";

  forEachFlow(pm.body, (flow) => {
    const at = flow.findIndex((n) => stepIdKey(n.stepId) === removedKey);
    if (at < 0) return;
    const rest = flow.slice(at + 1);
    for (const node of rest) {
      // `answersFan` is the resolver's own rule, so the scan reads past exactly
      // what it reads past — a terminator, a `dist` node, an unresolved owner.
      // Stopping early here means no pin is emitted and the edit lands with the
      // glyph re-anchored.
      const owner = ownerOfNode(node);
      if (!seeThrough(node) && answersFan(owner)) {
        if (owner?.form === "ditto") dependents.push(owner);
        break;
      }
      if (owner?.form === "ditto") {
        // An unresolved `|` — nothing to pin it to, and it still ends the scan.
        break;
      }
    }
    for (const node of rest) {
      if (seeThrough(node) || node.kind === "note") continue;
      if (node.kind === "call") {
        const owner = (node as CallNode).owner;
        if (owner.form === "snake") dependents.push(owner);
      }
      break; // the first node that leaves a tail settles it either way
    }
  });

  const out: TextEdit[] = [];
  for (const owner of dependents) {
    const glyph = owner.source;
    // Written form, not the resolved path: a membership alias must stay the
    // alias (§1.5), or the step lands in a different lane than it renders in.
    const name = owner.instance ?? owner.resolved?.join(".");
    if (!glyph || !name) continue; // nothing resolved to pin — already a TODO
    out.push({ start: glyph.start.offset, end: glyph.end.offset, newText: name });
  }
  return out;
}

function moveStepsEdits(
  src: string,
  pm: ProcessModel,
  m: { readonly op: "moveSteps"; readonly stepIds: readonly string[]; readonly anchorStepId: string; readonly position: "before" | "after" },
): TextEdit[] {
  const find = (key: string): ProcessIrNode | undefined => {
    let found: ProcessIrNode | undefined;
    forEachNode(pm.body, (n) => {
      if (stepIdKey(n.stepId) === key) found = n;
    });
    return found;
  };
  const anchor = find(m.anchorStepId);
  if (!anchor || !anchor.source) {
    refuse("no-span", `no anchor step '${m.anchorStepId}' in process '${pm.name || "(anonymous)"}'`);
  }
  const anchorSpan = anchor.source;

  // Resolve each id → its span, keeping the caller's order; drop unresolvable /
  // no-source ids, the anchor itself, and duplicates.
  const seen = new Set<string>();
  const kept: { key: string; span: SourceSpan }[] = [];
  for (const key of m.stepIds) {
    if (seen.has(key)) continue;
    seen.add(key);
    if (key === m.anchorStepId) continue;
    const n = find(key);
    if (!n || !n.source) continue;
    kept.push({ key, span: n.source });
  }

  // Drop a node whose span ENCLOSES the anchor (can't move a block onto an anchor
  // inside its own body — mirrors moveStep's spanWithin guard), and a node that
  // is a DESCENDANT of another kept node (its span within another's — a step
  // inside a moved block; the block already carries it).
  const survivors = kept.filter((k) => {
    if (spanWithin(anchorSpan, k.span) && !sameSpan(anchorSpan, k.span)) return false;
    return !kept.some((o) => o !== k && spanWithin(k.span, o.span) && !sameSpan(k.span, o.span));
  });
  if (survivors.length === 0) return [];

  // The insertion point + destination indent come from the anchor, exactly like
  // the single moveStep.
  const targetIndent = leadingIndent(src, anchorSpan.start.offset);
  const lead = m.position === "before";
  let insertAt: number;
  if (m.position === "after") {
    const nl = src.indexOf("\n", anchorSpan.end.offset);
    insertAt = nl === -1 ? anchorSpan.end.offset : nl;
  } else {
    insertAt = src.lastIndexOf("\n", anchorSpan.start.offset - 1) + 1;
  }

  // Cut each survivor (separator-aware). Two survivors on one `;`-line could have
  // overlapping removals — drop a removal that overlaps an already-collected one
  // (the earliest wins; nothing is torn). Its span is then also excluded from the
  // re-inserted block, so the two stay consistent.
  const removals: TextEdit[] = [];
  const placed: { span: SourceSpan }[] = [];
  for (const s of survivors) {
    const rem = removeMemberEdits(src, s.span.start.offset, s.span.end.offset);
    if (rem.some((e) => removals.some((c) => e.start < c.end && c.start < e.end))) continue;
    // A removal that would swallow the insertion anchor point would corrupt the
    // destination — skip it (defensive; the enclose/descendant drops already
    // exclude a survivor that contains the anchor).
    if (insertAt > s.span.start.offset && insertAt < s.span.end.offset) continue;
    removals.push(...rem);
    placed.push({ span: s.span });
  }
  if (placed.length === 0) return [];

  // One ordered, contiguous insertion: each survivor's raw text rebased to the
  // anchor indent, joined by newlines. `before` → `<block>\n` (above the anchor);
  // `after` → `\n<block>` (below it).
  const block = placed
    .map((p) => rebaseIndent(src.slice(p.span.start.offset, p.span.end.offset), leadingIndent(src, p.span.start.offset), targetIndent))
    .join("\n");
  const insertion: TextEdit = lead
    ? { start: insertAt, end: insertAt, newText: `${block}\n` }
    : { start: insertAt, end: insertAt, newText: `\n${block}` };
  return [...removals, insertion];
}

/** True when two spans cover the exact same byte range. */
function sameSpan(a: SourceSpan, b: SourceSpan): boolean {
  return a.start.offset === b.start.offset && a.end.offset === b.end.offset;
}

/** The open/close tokens for each wrap construct. The inner steps go between
 *  them, re-indented one level. Draft-floor placeholders (`TODO`, empty
 *  `catch`) are the author's to fill in. */
function wrapTokens(construct: WrapConstruct): { open: string; close: string } {
  switch (construct) {
    case "if":
      return { open: "if TODO {", close: "}" };
    case "each":
      return { open: "each TODO {", close: "}" };
    case "try":
      return { open: "try {", close: "} catch { }" };
    case "parallel":
      return { open: "parallel {", close: "}" };
    case "reversible":
      return { open: "reversible {", close: "}" };
  }
}

/** Wrap `firstNode`..`lastNode` (a contiguous sibling run) in `construct`.
 *  Byte-preserves the inner span and only re-indents it one level, so comments
 *  and nested subprocesses survive. Refuses a range that crosses a nesting
 *  boundary (any node partially overlapping the range). */
function wrapStepEdits(
  src: string,
  pm: ProcessModel,
  firstNode: ProcessIrNode,
  m: { readonly op: "wrapStep"; readonly stepId: string; readonly lastStepId?: string; readonly construct: WrapConstruct },
): TextEdit[] {
  const first = firstNode.source!;
  let last = first;
  if (m.lastStepId && m.lastStepId !== m.stepId) {
    let lastNode: ProcessIrNode | undefined;
    forEachNode(pm.body, (n) => {
      if (stepIdKey(n.stepId) === m.lastStepId) lastNode = n;
    });
    if (!lastNode) refuse("no-span", `no step '${m.lastStepId}' in process '${pm.name || "(anonymous)"}'`);
    if (!lastNode.source) refuse("no-span", `step '${m.lastStepId}' is synthesized — no source to edit`);
    last = lastNode.source;
  }
  const rangeStart = first.start.offset;
  const rangeEnd = last.end.offset;
  if (rangeEnd < rangeStart) refuse("range-crosses-block", `wrapStep range is inverted`);
  // Every node must sit fully inside or fully outside the range — a partial
  // overlap means the range crosses a block boundary, which the wrap would
  // corrupt (SPEC: control flow nests).
  forEachNode(pm.body, (n) => {
    const s = n.source;
    if (!s) return;
    const a = s.start.offset;
    const b = s.end.offset;
    const inside = a >= rangeStart && b <= rangeEnd;
    const outside = b <= rangeStart || a >= rangeEnd;
    if (!inside && !outside) {
      refuse("range-crosses-block", `wrapStep range crosses a nesting boundary at offset ${a}`);
    }
  });

  const { open, close } = wrapTokens(m.construct);
  const indent = leadingIndent(src, rangeStart);
  const inner = src.slice(rangeStart, rangeEnd);
  const lines = inner.split("\n");
  const body = lines
    .map((l, i) => (i === 0 ? `${indent}${INDENT}${l}` : `${INDENT}${l}`))
    .join("\n");
  const newText = `${open}\n${body}\n${indent}${close}`;
  return [{ start: rangeStart, end: rangeEnd, newText }];
}

/** Value ops applicable to an interface target (no add/delete/move here —
 *  delete is {@link synthesizeInterfaceDelete}; rename rides the LSP). */
export type InterfaceMutation =
  | { readonly op: "setDescription"; readonly text: string }
  | { readonly op: "setField"; readonly path: readonly string[]; readonly value: string }
  | { readonly op: "removeField"; readonly path: readonly string[] }
  | { readonly op: "setAspect"; readonly path: readonly string[]; readonly value: string }
  | { readonly op: "removeAspect"; readonly path: readonly string[] }
  | { readonly op: "setType"; readonly typeName: string };

/**
 * Interface value ops — a deliberate MIRROR of the module dispatcher's value
 * cases over the narrower `InterfaceBody` (same §4a preconditions, same
 * materialize-in-one-fragment rule for bare interfaces, same resolved-body
 * check before `drop`). Kept parallel rather than extracted: the module path
 * is fuzz-hardened, and the two bodies genuinely differ (no sub-decls here).
 */
export function synthesizeInterfaceMutation(
  src: string,
  loc: InterfaceLocation,
  op: InterfaceMutation,
): TextEdit[] {
  if (loc.materialization === "stub") {
    refuse("stub-target", `'${op.op}' on synthesized interface '${loc.decl.name}' — author it first`);
  }
  if (!loc.span) refuse("no-span", `interface '${loc.decl.name}' carries no source span`);

  const decl = loc.decl;
  const bodyless = loc.materialization === "bodyless";

  switch (op.op) {
    case "setDescription": {
      const entries = decl.body.descriptions.filter((d) => d.source);
      if (entries.length > 1) {
        refuse("multiple-descriptions", `'${decl.name}' has ${entries.length} description strings — edit in source`);
      }
      const memberIndent = memberIndentOf(src, loc.span, entries[0]?.source);
      const literal = renderDescription(op.text, memberIndent);
      if (entries.length === 1) {
        const s = entries[0]!.source!;
        return [{ start: s.start.offset, end: s.end.offset, newText: literal }];
      }
      if (bodyless) return materializeBodyEdit(src, decl, [literal]);
      return prependBodyMember(src, decl, literal);
    }

    case "setField": {
      refuseOnBadPath("field", op.path);
      if (bodyless) return materializeBodyEdit(src, decl, [`${op.path.join(".")}: ${op.value}`]);
      return setFieldCellEdit(src, decl, op.path, op.value);
    }

    case "removeField": {
      const authored = decl.body.fields.find((f) => samePath(f.path, op.path));
      if (authored?.source) {
        return removeMemberEdits(src, authored.source.start.offset, authored.source.end.offset);
      }
      const inherited = loc.iface.body.fields.some((f) => samePath(f.path, op.path));
      if (!inherited) {
        refuse("field-not-present", `'${decl.name}' has no field '${op.path.join(".")}' to remove`);
      }
      refuseOnBadPath("field", op.path); // the `drop` line splices the path verbatim (r40)
      const line = `drop ${op.path.join(".")}`;
      if (bodyless) return materializeBodyEdit(src, decl, [line]);
      return insertBodyMember(src, decl, line);
    }

    case "setAspect": {
      refuseOnBadPath("aspect", op.path);
      const authored = decl.body.aspects.find((a) => samePath(a.path, op.path));
      if (authored?.source) {
        const anchor = (authored.pathSource ?? authored.source).start.offset;
        const colon = src.indexOf(":", anchor);
        if (colon >= 0 && colon < authored.source.end.offset) {
          return [{ start: colon + 1, end: authored.source.end.offset, newText: ` ${op.value}` }];
        }
        const s = authored.source;
        const keyword = src.startsWith("aspect", s.start.offset);
        const head = keyword ? `aspect ${op.path.join(".")}` : op.path.join(".");
        return [{ start: s.start.offset, end: s.end.offset, newText: `${head}: ${op.value}` }];
      }
      const line = `aspect ${op.path.join(".")}: ${op.value}`;
      if (bodyless) return materializeBodyEdit(src, decl, [line]);
      return insertBodyMember(src, decl, line);
    }

    case "removeAspect": {
      const authored = decl.body.aspects.find((a) => samePath(a.path, op.path));
      if (!authored?.source) {
        refuse("aspect-not-authored", `aspect '${op.path.join(".")}' is not authored on '${decl.name}'`);
      }
      return removeMemberEdits(src, authored.source.start.offset, authored.source.end.offset);
    }

    case "setType": {
      refuseOnBadWord("kind", op.typeName); // mirror of the module setType guard (r38)
      const s = decl.typeSource ?? loc.span;
      return [{ start: s.start.offset, end: s.start.offset + decl.typeName.length, newText: op.typeName }];
    }
  }
}

/** Delete an interface declaration (prefix-extent aware). */
export function synthesizeInterfaceDelete(src: string, loc: InterfaceLocation): TextEdit[] {
  if (loc.materialization === "stub") {
    refuse("stub-target", `cannot delete synthesized interface '${loc.decl.name}'`);
  }
  const extent = loc.extent ?? loc.span;
  if (!extent) refuse("no-span", `interface '${loc.decl.name}' carries no source span`);
  return removeMemberEdits(src, extent.start.offset, extent.end.offset);
}

/**
 * Scaffold a NEW process carrying its first step (§3.3 "new process…"):
 * `anchor` = the common-ancestor module to nest into (its body is
 * materialized when bodyless), or null for a top-level append at the end of
 * the file. `name` = null emits the zero-prompt ANONYMOUS form
 * (`process { … }` — legal and debt-free, §4.13.1).
 */
export function scaffoldProcessEdit(
  src: string,
  anchor: ModuleDecl | null,
  name: string | null,
  from: string,
  to: string,
): TextEdit[] {
  if (name != null) refuseOnBadWord("name", name);
  const head = name ? `process ${name}` : "process";
  // Every arm is parse-guarded: from/to refs are spliced verbatim, and a
  // keyword-named module (a legal decl name) can't appear in step position (r40).
  if (anchor) {
    const block = `${head} {\n${INDENT}${from} > ${to}\n}`;
    // insertBodyMember indents the first line to the body; inner lines keep
    // their own relative nesting (parse-correct; the formatter tidies).
    if (anchor.hasBody === false) {
      return guardInsertParse(src, materializeBodyEdit(src, anchor, [`${head} {`, `${INDENT}${from} > ${to}`, `}`]), "scaffoldProcess");
    }
    return guardInsertParse(src, insertBodyMember(src, anchor, block), "scaffoldProcess");
  }
  const sep = src.endsWith("\n") ? "\n" : "\n\n";
  return guardInsertParse(
    src,
    [{ start: src.length, end: src.length, newText: `${sep}${head} {\n${INDENT}${from} > ${to}\n}\n` }],
    "scaffoldProcess",
  );
}

/** Append a NEW top-level module declaration at the end of the file
 *  (§3.4 root-level adds — deterministic, no modal file picking). */
export function appendTopLevelModuleEdit(
  src: string,
  kind: string,
  name: string | undefined,
  braced: boolean | undefined,
): TextEdit[] {
  refuseOnBadWord("kind", kind);
  if (name !== undefined) refuseOnBadWord("name", name);
  const head = name ? `${kind} ${name}` : kind;
  const block = braced ? `${head} {}` : head;
  const sep = src.endsWith("\n") ? "\n" : "\n\n";
  return [{ start: src.length, end: src.length, newText: `${sep}${block}\n` }];
}

/**
 * The shortest segment-aligned path suffix that uniquely names `target`
 * among `allPaths` (mirrors §4.11 name hoisting, so the emitted reference
 * resolves — residual shadowing surfaces as the ambiguity diagnostic, never
 * silently misbinds).
 */
export function shortestUniqueSuffix(
  allPaths: readonly (readonly string[])[],
  target: readonly string[],
): string {
  // A target that isn't IN the path set can still find a "unique" suffix —
  // one that resolves to a DIFFERENT module. Emit the full path instead
  // (worst case an unresolved-ref diagnostic, never a silent misbind).
  const present = allPaths.some(
    (p) => p.length === target.length && p.every((s, i) => s === target[i]),
  );
  if (!present) return target.join(".");
  for (let take = 1; take <= target.length; take++) {
    const suffix = target.slice(target.length - take);
    let matches = 0;
    for (const p of allPaths) {
      if (p.length < take) continue;
      const tail = p.slice(p.length - take);
      if (tail.every((s, i) => s === suffix[i])) matches++;
    }
    if (matches === 1) return suffix.join(".");
  }
  return target.join(".");
}

// =============================================================================
// View WYSIWYG builder — ordered-clause ops over ViewDeclV2
// =============================================================================

/**
 * View ops are INDEX-addressed: a view's clauses are ordered and order is
 * semantic (`show → hide → group` pipelines, `on` narrows after show/hide),
 * so unlike §4.9 module bodies there is a real "insert between" here.
 * Clause TEXT is caller-rendered (the selector algebra is the UI's/driver's
 * vocabulary); validity surfaces through the standard recompile invariant.
 */
export type ViewMutation =
  | { readonly op: "appendClause"; readonly clauseText: string }
  | { readonly op: "insertClauseAt"; readonly index: number; readonly clauseText: string }
  | { readonly op: "replaceClause"; readonly index: number; readonly clauseText: string }
  | { readonly op: "removeClause"; readonly index: number }
  | { readonly op: "setViewField"; readonly path: readonly string[]; readonly value: string }
  | { readonly op: "rename"; readonly name: string }
  | { readonly op: "deleteDecl" }
  /** S16 persistence (view channel): upsert `style <target> { pin x, y }` per
   *  entry — BATCHED so one persist gesture is one guard cycle / one journal
   *  entry (a second `mutateView` before the recompile would read stale). */
  | { readonly op: "setPins"; readonly pins: readonly ViewPin[] }
  /** S16/S18 channel split: one presentation member (`rename "X"`, `color: red`)
   *  upserted under `style <selectorText> { … }` in the VIEW body — the model
   *  declaration stays untouched. */
  | { readonly op: "setStyleMember"; readonly selectorText: string; readonly member: string }
  /** S16 persistence (knob channel): write changed knob values as a view
   *  INSTANCE. When the target view is a PARENT, a new
   *  `view <Parent> <instanceName> { <bindings> }` is appended at file top
   *  level; when the target IS an instance, its bindings are upserted in place
   *  (`instanceName` ignored). Binding values are caller-rendered literals,
   *  like `setStyleMember`'s member text. */
  | { readonly op: "upsertInstance"; readonly instanceName: string; readonly bindings: readonly ViewInstanceBinding[] }
  /** S17 table column editing: rewrite the column list of the `table` clause at
   *  `index` (in `view.clauses`) — span-precise. Kept columns (`{ from }`) reuse
   *  their original source bytes verbatim (calc exprs + formatting survive); a
   *  title override or a brand-new `{ getter }` column is rendered from the spec.
   *  `sortBy`: OMITTED = keep the clause's existing `sort by`; `null` = drop it;
   *  a getter = set it. Reorder = permute `from`; remove = omit; rename =
   *  `from` + title; add = a `getter` entry. */
  | {
      readonly op: "setTableColumns";
      readonly index: number;
      readonly columns: readonly TableColumnSpec[];
      readonly sortBy?: TableGetterSpec | null;
    }
  /** View-configurator "Save as view" edit-in-place: replace the view's CLAUSE
   *  region with a fresh clause list, PRESERVING the header zone (`title:` fields
   *  + `@knob` decls — they live inside the braces but are NOT clauses) before the
   *  first clause and any trailing content after the last. The structured builder
   *  can't represent comments interleaved BETWEEN clauses (those are dropped).
   *  Empty `clauseTexts` removes the clause region. */
  | { readonly op: "setClauses"; readonly clauseTexts: readonly string[] }
  /** The header-zone twin of `setClauses`: replace the view's `knob` DECL region
   *  with a fresh decl list. Knob decls are header members, so they must land
   *  BEFORE the first clause — a view that has none yet gets them inserted after
   *  its last header member (else at the top of the body). Empty `knobTexts`
   *  removes the knob region. Decl text is caller-rendered, like clause text. */
  | { readonly op: "setKnobs"; readonly knobTexts: readonly string[] };

/** A getter reference for a table column / sort key (mirrors `ExprGetter`, sans
 *  spans — the write path builds these, it doesn't parse them). */
export interface TableGetterSpec {
  readonly sigil: GetterSigil;
  readonly path: readonly string[];
  readonly capabilityArgs?: string;
}

/** One entry in a rewritten table column list: reuse an existing column
 *  (`from`, optionally re-titled) or author a brand-new getter column. */
export type TableColumnSpec =
  | { readonly from: number; readonly title?: string | null }
  | { readonly getter: TableGetterSpec; readonly title?: string };

/** One persisted hand-placement: the style-clause subject + board position. */
export interface ViewPin {
  readonly targetName: string;
  readonly x: number;
  readonly y: number;
}

/**
 * Refuse a style-clause subject that cannot address anything (defense-in-depth
 * behind the app's board-id→origin mapping — mirrors
 * {@link refuseOnMemberNameCollision}: the engine write path is the last line
 * of defense, so no caller can corrupt text):
 *
 *  - text that does not PARSE as a selector would write unparseable bytes into
 *    the view body (the projected-copy bug: a synthetic board render id
 *    `g1::Shop.Ledger` leaking through `refFor` verbatim) — always refused;
 *  - with the resolved model at hand, a plain-ref subject that resolves to NO
 *    node is refused too (a stale/unknown name — the write would be dead text).
 *    Non-ref selector subjects (type names resolve as refs; `violating <P>`,
 *    arrows, predicates do not) pass: their emptiness is time-varying, not
 *    proof of a bad write.
 */
function refuseOnUnaddressableStyleSubject(
  subject: string,
  view: ViewDeclV2,
  model: ResolvedModel | null | undefined,
): void {
  const { selector, errors } = parseSelectorSource(subject);
  if (errors.length > 0) {
    refuse(
      "bad-style-subject",
      `style subject '${subject}' is not a valid selector: ${errors[0]!.message}`,
    );
  }
  if (!model || selector.kind !== "ref") return;
  const r = evalSelector(selector, {
    universe: buildNodeUniverse(model),
    ...(view.ownerPath && view.ownerPath.length > 0 ? { scopePath: view.ownerPath } : {}),
  });
  if (r.sort === "node" && r.nodes.size === 0) {
    refuse(
      "unknown-style-subject",
      `style subject '${subject}' resolves to nothing in the model`,
    );
  }
}

/** Render a getter reference back to source text: `@repo.url`, `@@team`,
 *  `@ver.changes(last 90 days)`. */
function renderGetter(g: TableGetterSpec): string {
  const base = `${g.sigil}${g.path.join(".")}`;
  return g.capabilityArgs !== undefined ? `${base}(${g.capabilityArgs})` : base;
}

/** Render ONE column-list entry (no indent). Kept columns without a title
 *  override reuse their original bytes verbatim; everything else is authored. */
function renderColumnSpec(src: string, table: TableClause, spec: TableColumnSpec): string {
  if ("getter" in spec) {
    const title = spec.title !== undefined ? ` ${JSON.stringify(spec.title)}` : "";
    return `column ${renderGetter(spec.getter)}${title}`;
  }
  const col = table.columns[spec.from];
  if (!col) {
    refuse("table-column-range", `table column index ${spec.from} is out of range (0..${table.columns.length - 1})`);
  }
  // No title override → the column is unchanged; reuse its exact source bytes so
  // calc expressions and formatting survive untouched.
  if (spec.title === undefined) {
    if (!col.source) refuse("no-span", "table column carries no source span");
    return src.slice(col.source.start.offset, col.source.end.offset).trim();
  }
  if (col.kind === "calc") {
    if (spec.title === null) refuse("table-column-title", "a calc column requires a title");
    if (!col.expr.source) refuse("no-span", "calc column expression carries no source span");
    const expr = src.slice(col.expr.source.start.offset, col.expr.source.end.offset).trim();
    return `column ${JSON.stringify(spec.title)}: ${expr}`;
  }
  // Getter column re-title: `null` drops the title (falls back to the humanized
  // getter name at render); a string sets it.
  const title = spec.title !== null ? ` ${JSON.stringify(spec.title)}` : "";
  return `column ${renderGetter(col.getter)}${title}`;
}

/**
 * Render a whole `table { … }` clause from a rewritten column list. Pure over
 * `(src, table, columns, sortBy)`; the fuzz suite drives this directly. `sortBy`
 * `undefined` keeps the clause's existing `sort by`, `null` drops it, a getter
 * sets it. `clauseIndent` is the indent of the `table` line (inner members get
 * one more `INDENT`).
 */
export function renderTableClause(
  src: string,
  table: TableClause,
  columns: readonly TableColumnSpec[],
  sortBy: TableGetterSpec | null | undefined,
  clauseIndent: string,
): string {
  if (columns.length === 0) refuse("empty-table-columns", "a table needs at least one column");
  const memberIndent = clauseIndent + INDENT;
  const lines = columns.map((spec) => memberIndent + renderColumnSpec(src, table, spec));
  const effectiveSort = sortBy === undefined ? table.sortBy : sortBy;
  if (effectiveSort) lines.push(`${memberIndent}sort by ${renderGetter(effectiveSort)}`);
  return `table {\n${lines.join("\n")}\n${clauseIndent}}`;
}

export function synthesizeViewMutation(
  src: string,
  view: ViewDeclV2,
  m: ViewMutation,
  /** The resolved model the view compiled against. When given, style-subject
   *  ops ALSO refuse a plain-ref subject that resolves to no model node (see
   *  {@link refuseOnUnaddressableStyleSubject}); without it only the syntax
   *  guard applies. Pass it wherever available — the write path is the last
   *  line of defense against text corruption. */
  model?: ResolvedModel | null,
): TextEdit[] {
  if (!view.source) refuse("no-span", `view '${view.name}' carries no source span`);

  const clauseAt = (index: number): SourceSpan => {
    const span = view.clauses[index]?.source;
    if (!span) {
      refuse("no-span", `view '${view.name}' has no clause ${index} (of ${view.clauses.length})`);
    }
    return span;
  };

  switch (m.op) {
    case "appendClause": {
      const a = viewMemberAnchors(src, view);
      return [insertViewMemberEdit(src, view, a.lastClauseEnd ?? a.lastHeaderEnd, m.clauseText)];
    }

    case "insertClauseAt": {
      // Insert BEFORE clause[index] on its own line (append covers the end).
      const span = clauseAt(m.index);
      const indent = leadingIndent(src, span.start.offset);
      const lineStart = src.lastIndexOf("\n", span.start.offset - 1) + 1;
      return [{ start: lineStart, end: lineStart, newText: `${indent}${m.clauseText}\n` }];
    }

    case "replaceClause": {
      const span = clauseAt(m.index);
      return [{ start: span.start.offset, end: span.end.offset, newText: m.clauseText }];
    }

    case "removeClause": {
      const span = clauseAt(m.index);
      return removeMemberEdits(src, span.start.offset, span.end.offset);
    }

    case "setViewField": {
      const existing = view.header.fields.find((f) => samePath(f.path, m.path));
      if (existing?.source) {
        const colon = src.indexOf(":", existing.source.start.offset);
        if (colon >= 0 && colon < existing.source.end.offset) {
          return [{ start: colon + 1, end: existing.source.end.offset, newText: ` ${m.value}` }];
        }
      }
      // Header zone: after the last header member; a header member after the
      // first clause is a VIEW_HEADER_AFTER_CLAUSE diagnostic, so with
      // clauses present but no header the line goes BEFORE clause 0.
      const a = viewMemberAnchors(src, view);
      const line = `${m.path.join(".")}: ${m.value}`;
      if (a.lastHeaderEnd !== null) {
        return [insertViewMemberEdit(src, view, a.lastHeaderEnd, line)];
      }
      const first = view.clauses[0]?.source;
      if (first) {
        const indent = leadingIndent(src, first.start.offset);
        const lineStart = src.lastIndexOf("\n", first.start.offset - 1) + 1;
        return [{ start: lineStart, end: lineStart, newText: `${indent}${line}\n` }];
      }
      return [insertViewMemberEdit(src, view, null, line)];
    }

    case "rename": {
      const s = view.nameSource;
      if (!s) refuse("no-span", `view '${view.name}' has no name span`);
      return [{ start: s.start.offset, end: s.end.offset, newText: m.name }];
    }

    case "setPins": {
      // Last-wins per target, then compose one upsertPinEdit per pin against
      // the SAME src. Fresh-clause inserts are zero-width edits at the same
      // anchor (never overlapping); existing-pin replacements hit disjoint
      // spans — applyTextEdits validates both invariants.
      const byTarget = new Map<string, ViewPin>();
      for (const p of m.pins) byTarget.set(p.targetName, p);
      for (const p of byTarget.values()) refuseOnUnaddressableStyleSubject(p.targetName, view, model);
      const edits: TextEdit[] = [];
      for (const p of byTarget.values()) {
        edits.push(...upsertPinEdit(src, view, p.targetName, p.x, p.y));
      }
      return edits;
    }

    case "setStyleMember":
      refuseOnUnaddressableStyleSubject(m.selectorText, view, model);
      return upsertStyleClauseEdit(src, view, m.selectorText, m.member);

    case "upsertInstance":
      return upsertViewInstance(src, view, m.instanceName, m.bindings);

    case "setTableColumns": {
      const clause = view.clauses[m.index];
      if (!clause || clause.kind !== "table") {
        refuse("not-table-clause", `view clause ${m.index} is not a table clause`);
      }
      const span = clauseAt(m.index);
      const indent = leadingIndent(src, span.start.offset);
      // `sortBy` OMITTED (not in the op) keeps the existing sort; present-and-null
      // drops it; a getter sets it.
      const sortBy = "sortBy" in m ? (m.sortBy ?? null) : undefined;
      const text = renderTableClause(src, clause, m.columns, sortBy, indent);
      // Parse-guarded: add-column getters arrive as free text (`@release date`,
      // `@$w`) rendered verbatim into the clause — the one column op that could
      // corrupt the file (r40).
      return guardInsertParse(
        src,
        [{ start: span.start.offset, end: span.end.offset, newText: text }],
        m.op,
      );
    }

    case "setClauses": {
      // Header-SAFE whole-clause replace: the header zone (`title:` fields +
      // `@knob` decls) lives inside the braces but is NOT a clause, so we replace
      // ONLY the span from the first clause to the last — everything before it
      // (header) and after it (trailing content) is preserved.
      const first = view.clauses[0]?.source;
      const last = view.clauses[view.clauses.length - 1]?.source;
      if (!first || !last) {
        // Header-only view (no clause to anchor to): insert the clauses after the
        // header zone. Empty request → nothing to do.
        if (m.clauseTexts.length === 0) return [];
        const a = viewMemberAnchors(src, view);
        const block = m.clauseTexts.join(`\n${a.bodyIndent}`);
        return [insertViewMemberEdit(src, view, a.lastClauseEnd ?? a.lastHeaderEnd, block)];
      }
      if (m.clauseTexts.length === 0) {
        return removeMemberEdits(src, first.start.offset, last.end.offset);
      }
      const indent = leadingIndent(src, first.start.offset);
      const newText = m.clauseTexts.join(`\n${indent}`);
      return [{ start: first.start.offset, end: last.end.offset, newText }];
    }

    case "setKnobs": {
      // The header-zone mirror of `setClauses`: replace ONLY the span from the
      // first knob decl to the last, so `title:` fields, descriptions and every
      // clause keep their bytes. A block-form entry's own span starts at its
      // NAME (no `knob` keyword), so the FIRST decl's start is the region start
      // only when it is the keyword form — hence the enclosing-block widening
      // below, which walks back over a `knob {` wrapper.
      const spans = view.header.knobs.map((k) => k.source).filter((s): s is SourceSpan => !!s);
      if (spans.length === 0) {
        if (m.knobTexts.length === 0) return [];
        const a = viewMemberAnchors(src, view);
        const block = m.knobTexts.join(`\n${a.bodyIndent}`);
        return [insertViewMemberEdit(src, view, a.lastHeaderEnd, block)];
      }
      const start = knobRegionStart(src, Math.min(...spans.map((s) => s.start.offset)));
      const lastSpan = spans.reduce((a, b) => (b.end.offset > a.end.offset ? b : a));
      const lastEnd = lastSpan.end.offset;
      // Whether there is a `}` to swallow is decided by the LAST entry, not the
      // first: a block-form entry's span stops at the entry, inside the wrapper.
      // Keying it off the START instead only worked while every knob shared one
      // form — `knob depth: 2` followed by a `knob { … }` block left the
      // wrapper's brace stranded, which closed the VIEW early. Widening
      // unconditionally is equally wrong: it would eat the view's own brace
      // after a trailing keyword-form knob.
      const lastIsBlockForm =
        knobRegionStart(src, lastSpan.start.offset) !== lastSpan.start.offset;
      const end = lastIsBlockForm ? knobBlockEnd(src, lastEnd) : lastEnd;
      if (m.knobTexts.length === 0) return removeMemberEdits(src, start, end);
      const indent = leadingIndent(src, start);
      return [{ start, end, newText: m.knobTexts.join(`\n${indent}`) }];
    }

    case "deleteDecl": {
      const extent = view.extentSource ?? view.source;
      return removeMemberEdits(src, extent.start.offset, extent.end.offset);
    }
  }
}

/** Widen a knob-region start back over a `knob {` block wrapper: a block-form
 *  entry's span starts at its NAME, so replacing from there would strand the
 *  `knob {` header. Returns `at` unchanged for the keyword form. */
function knobRegionStart(src: string, at: number): number {
  const before = src.slice(0, at);
  const brace = before.lastIndexOf("{");
  if (brace < 0) return at;
  const head = before.slice(0, brace).trimEnd();
  return /(^|[^\p{ID_Continue}])knob$/u.test(head) ? head.length - "knob".length : at;
}

/** Widen a knob-region end forward over a `knob { … }` block's closing brace —
 *  which usually sits on its own line after the last entry. */
function knobBlockEnd(src: string, at: number): number {
  const m = /^\s*\}/.exec(src.slice(at));
  return m ? at + m[0].length : at;
}

// =============================================================================
// BPMN lane/pool WYSIWYG builder — ordered-clause ops over a flow body
// =============================================================================

/**
 * Flow-body ops mutate the `lane`/`pool` clause list inside a
 * `flow <Ref> bpmn { … }` clause (itself one member of a view). Order is
 * semantic (lanes claim performers in document order; pools group), so — like
 * {@link ViewMutation} — there is a real "insert between" and a reorder. Clause
 * TEXT is caller-rendered (`lane "X": Y`, `pool Ref`); validity surfaces
 * through the standard recompile invariant.
 *
 * These drive the LANE-CONTAINER gestures (add / rename / remove / reorder /
 * create-pool). Reassigning a STEP to another lane is NOT here — a lane is a
 * projection of the step's owner, so that is an owner model-edit (`replaceStep`).
 */
export type FlowBodyMutation =
  | { readonly op: "appendClause"; readonly clauseText: string }
  | { readonly op: "insertClauseAt"; readonly index: number; readonly clauseText: string }
  | { readonly op: "replaceClause"; readonly index: number; readonly clauseText: string }
  | { readonly op: "removeClause"; readonly index: number }
  /** Retitle the clause at `index` PRESERVING its selector bytes — the inline
   *  rename gesture. A `lane <sel>` becomes `lane "Title": <sel>` (one merged
   *  lane, same membership for a single-element lane); a `lane|pool "Old": <sel>`
   *  swaps only the title. Refuses clauses whose title derives from their
   *  selector (`lane by`, `pool <Ref>`, `pool by`). */
  | { readonly op: "retitleClause"; readonly index: number; readonly title: string }
  /** Move the clause at `from` to land at `to` (arrayMove semantics over the
   *  current body order) — the drag-reorder gesture. */
  | { readonly op: "reorderClause"; readonly from: number; readonly to: number };

export function synthesizeFlowBodyMutation(
  src: string,
  flow: FlowClause,
  m: FlowBodyMutation,
): TextEdit[] {
  if (!flow.source) refuse("no-span", "flow clause carries no source span");
  if (flow.mode !== "bpmn" || !flow.body) {
    refuse("not-bpmn-flow", "flow-body edits require a `flow … bpmn { … }` clause with a body");
  }
  const body = flow.body;
  const clauseAt = (index: number): SourceSpan => {
    const span = body[index]?.source;
    if (!span) refuse("no-span", `flow body has no clause ${index} (of ${body.length})`);
    return span;
  };

  switch (m.op) {
    case "appendClause":
      return [insertFlowMemberEdit(src, flow, m.clauseText)];

    case "insertClauseAt": {
      if (m.index >= body.length) return [insertFlowMemberEdit(src, flow, m.clauseText)];
      const span = clauseAt(m.index);
      const indent = leadingIndent(src, span.start.offset);
      const lineStart = src.lastIndexOf("\n", span.start.offset - 1) + 1;
      return [{ start: lineStart, end: lineStart, newText: `${indent}${m.clauseText}\n` }];
    }

    case "replaceClause": {
      const span = clauseAt(m.index);
      return [{ start: span.start.offset, end: span.end.offset, newText: m.clauseText }];
    }

    case "removeClause": {
      const span = clauseAt(m.index);
      return removeMemberEdits(src, span.start.offset, span.end.offset);
    }

    case "retitleClause": {
      const clause = body[m.index];
      if (!clause) refuse("no-span", `flow body has no clause ${m.index} (of ${body.length})`);
      const span = clauseAt(m.index);
      if (
        clause.kind !== "lane-expand" &&
        clause.kind !== "lane-merge" &&
        clause.kind !== "pool-merge"
      ) {
        refuse("not-retitleable", `cannot retitle a ${clause.kind} clause — its title derives from its selector`);
      }
      const sel = clause.selector.source;
      if (!sel) refuse("no-span", "flow body clause selector carries no source span");
      const head = clause.kind === "pool-merge" ? "pool" : "lane";
      const selText = src.slice(sel.start.offset, sel.end.offset).trim();
      return [{ start: span.start.offset, end: span.end.offset, newText: `${head} ${JSON.stringify(m.title)}: ${selText}` }];
    }

    case "reorderClause": {
      if (body.length < 2 || m.from === m.to) return [];
      const a = flowBodyAnchors(src, flow);
      const spans = a.clauseSpans;
      if (spans.some((s) => !s)) refuse("no-span", "flow body clause carries no source span");
      const order = [...spans.keys()];
      const [moved] = order.splice(m.from, 1);
      order.splice(m.to, 0, moved!);
      const texts = order.map((i) => src.slice(spans[i]!.start.offset, spans[i]!.end.offset));
      const first = spans[0]!.start.offset;
      const last = spans[spans.length - 1]!.end.offset;
      return [{ start: first, end: last, newText: texts.join(`\n${a.bodyIndent}`) }];
    }
  }
}

/**
 * Scaffold a NEW view. Default seed `show *` is the natural WYSIWYG starting
 * point (show everything, then hide/group/style interactively); saving an
 * in-browser DRAFT passes its full clause list. `anchor` = a module decl to
 * nest into (module-scoped view), else a top-level append.
 */
export function scaffoldViewEdit(
  src: string,
  anchor: ModuleDecl | null,
  name: string,
  seedClauses: readonly string[] = ["show *"],
): TextEdit[] {
  const inner = seedClauses.map((c) => `${INDENT}${c}`);
  const block = `view ${name} {\n${inner.join("\n")}\n}`;
  if (anchor) {
    if (anchor.hasBody === false) {
      return materializeBodyEdit(src, anchor, [`view ${name} {`, ...inner, `}`]);
    }
    return insertBodyMember(src, anchor, block);
  }
  const sep = src.endsWith("\n") ? "\n" : "\n\n";
  return [{ start: src.length, end: src.length, newText: `${sep}${block}\n` }];
}

/**
 * Append a pre-serialized `test` block (from `printTest`) at the top level of a
 * file — the P8 "Save as test" write. A test decl is legal at top level (§5), so
 * a plain root append is the whole edit; the block lands after a blank line
 * separating it from the prior declaration.
 */
export function scaffoldTestEdit(src: string, block: string): TextEdit[] {
  if (src.trim() === "") return [{ start: src.length, end: src.length, newText: `${block}\n` }];
  const sep = src.endsWith("\n") ? "\n" : "\n\n";
  return [{ start: src.length, end: src.length, newText: `${sep}${block}\n` }];
}

// =============================================================================
// Headless workspace writes (W4, plan §9) — CLI / MCP / CI drivers
// =============================================================================

/**
 * Apply ONE module mutation against an in-memory workspace (the exact shape
 * `loadViewer`/`readArchVfs` produce: absolute "/"-keyed file map + the
 * resolved model compiled FROM those files). Pure files-in/files-out — the
 * caller owns disk IO. Returns the touched files' new content. Throws
 * {@link MutationRefusedError} on §4a refusals and unknown targets.
 */
export function applyWorkspaceModuleMutation(
  model: ResolvedModel,
  files: ReadonlyMap<string, string>,
  targetId: string,
  op: ModuleMutation,
): Map<string, string> {
  const lookup = createSpanLookup(model);
  // v0.11: a WRITE never first-match-wins — a bare target naming twins across
  // spaces refuses with the unambiguous spellings (rev-1 F8).
  const twins = lookup.moduleTwins(targetId);
  if (twins.length > 1) {
    refuse(
      "ambiguous-target",
      `'${targetId}' names ${twins.length} modules across spaces — qualify it: ${twins.join(", ")}`,
    );
  }
  const loc = lookup.moduleFor(targetId);
  // Target auto-detect: a dotted id that isn't a module may be an INTERFACE
  // path (`Orders.place`) — value ops route to the interface dispatcher.
  if (!loc) {
    const itwins = lookup.interfaceTwins(targetId);
    if (itwins.length > 1) {
      refuse(
        "ambiguous-target",
        `'${targetId}' names ${itwins.length} interfaces across spaces — qualify it: ${itwins.join(", ")}`,
      );
    }
    const iloc = lookup.interfaceFor(targetId);
    if (iloc && isInterfaceOp(op)) {
      const ifile = iloc.file;
      const isrc = ifile !== undefined ? files.get(ifile) : undefined;
      if (ifile === undefined || isrc === undefined) {
        refuse("no-span", `'${targetId}' resolves outside the workspace files`);
      }
      const iedits = synthesizeInterfaceMutation(isrc, iloc, op as InterfaceMutation);
      return new Map([[ifile, applyTextEdits(isrc, iedits)]]);
    }
    refuse("no-span", `no module or interface '${targetId}' in the workspace`);
  }
  const file = loc.file;
  const src = file !== undefined ? files.get(file) : undefined;
  if (file === undefined || src === undefined) {
    refuse("no-span", `'${targetId}' resolves outside the workspace files (stdlib/template?)`);
  }
  const edits = synthesizeModuleMutation(src, loc, op);
  return new Map([[file, applyTextEdits(src, edits)]]);
}

function isInterfaceOp(op: ModuleMutation): boolean {
  return (
    op.op === "setDescription" ||
    op.op === "setField" ||
    op.op === "removeField" ||
    op.op === "setAspect" ||
    op.op === "removeAspect" ||
    op.op === "setType"
  );
}

// =============================================================================
// Move + reference requalification (W3, plan §3.2)
// =============================================================================

/**
 * Move a module declaration under a new parent (or to its file's root) and
 * requalify the references that pointed INTO the moved subtree. Multi-file:
 * origin delete (prefix-extent aware), destination insert (materializing a
 * bodyless parent), and per-file span rewrites of process callers/callees,
 * `in <Parent>` clauses, and textually-qualified aspect refs. A written form
 * that stays byte-identical post-move (a still-unique bare suffix) emits no
 * edit; anything the rewrite can't see becomes a visible diagnostic — the
 * plan's accepted safety model, never silent corruption.
 */
export function computeMoveEdits(
  model: ResolvedModel,
  files: ReadonlyMap<string, string>,
  targetId: string,
  newParentId: string | null,
): Map<string, TextEdit[]> {
  const indexes = buildIndexes(model);
  const resolveId = (id: string): ResolvedModule | undefined => {
    const raw = id.startsWith("#") ? id.slice(1) : id;
    return indexes.modulesById.get(raw as StableId) ?? indexes.modulesByPath.get(raw);
  };

  const target = resolveId(targetId);
  if (!target) refuse("no-span", `no module '${targetId}' in the model`);
  if (target.synthesized || isZeroSpan(target.decl.source)) {
    refuse("stub-target", `cannot move synthesized stub '${target.name}'`);
  }
  const oldPath = indexes.pathOfModule.get(target);
  if (!oldPath || !target.decl.source) refuse("no-span", `module '${targetId}' has no source`);
  const originFile = target.decl.source.file;
  if (!originFile || !files.has(originFile)) refuse("no-span", `origin file unknown for '${targetId}'`);
  const originSrc = files.get(originFile)!;

  const newParent = newParentId ? resolveId(newParentId) : null;
  if (newParentId && !newParent) refuse("no-span", `no module '${newParentId}' in the model`);
  if (newParent && (newParent.synthesized || isZeroSpan(newParent.decl.source))) {
    refuse("stub-target", `cannot move into synthesized stub '${newParent.name}'`);
  }
  const newParentPath = newParent ? indexes.pathOfModule.get(newParent)! : [];
  if (
    newParentPath.length >= oldPath.length &&
    oldPath.every((s, i) => newParentPath[i] === s)
  ) {
    refuse("cycle", `cannot move '${target.name}' into its own subtree`);
  }
  // No-op: the target already lives under this parent. Beyond being useless,
  // synthesizing it would anchor the insert INSIDE the delete range (the
  // target may be the parent's last member) — fuzz-caught overlap.
  const currentParentPath = oldPath.slice(0, -1);
  if (
    currentParentPath.length === newParentPath.length &&
    currentParentPath.every((s, i) => newParentPath[i] === s)
  ) {
    refuse("same-parent", `'${target.name}' is already under '${newParentPath.join(".") || "(root)"}'`);
  }
  const siblings = newParent ? newParent.modules : model.modules;
  if (siblings.some((m) => m !== target && m.name === target.name)) {
    refuse("name-collision", `'${newParentPath.join(".") || "(root)"}' already holds '${target.name}'`);
  }

  const oldPrefix = oldPath.join(".");
  const newPath = [...newParentPath, target.name];
  const remap = (p: readonly string[]): readonly string[] =>
    p.length >= oldPath.length && oldPath.every((s, i) => p[i] === s)
      ? [...newPath, ...p.slice(oldPath.length)]
      : p;
  // Post-move path universe for minimal-suffix reference computation.
  const newAllPaths: (readonly string[])[] = [];
  for (const p of indexes.pathOfModule.values()) newAllPaths.push(remap(p));
  const minimalRef = (p: readonly string[]): string => shortestUniqueSuffix(newAllPaths, remap(p));

  const extent = target.decl.extentSource ?? target.decl.source;
  const byFile = new Map<string, TextEdit[]>();
  const push = (file: string, edit: TextEdit): void => {
    const list = byFile.get(file) ?? [];
    list.push(edit);
    byFile.set(file, list);
  };
  const insideExtent = (file: string, span: SourceSpan): boolean =>
    file === originFile && span.start.offset >= extent.start.offset && span.end.offset <= extent.end.offset;

  // --- reference requalification -------------------------------------------
  const rewriteSpan = (span: SourceSpan | undefined, newWritten: string): void => {
    if (!span?.file) return;
    const src = files.get(span.file);
    if (src === undefined || insideExtent(span.file, span)) return;
    if (src.slice(span.start.offset, span.end.offset) === newWritten) return; // byte-identical → no edit
    push(span.file, { start: span.start.offset, end: span.end.offset, newText: newWritten });
  };
  const inSubtree = (p: readonly string[]): boolean =>
    p.length >= oldPath.length && oldPath.every((s, i) => p[i] === s);

  for (const pm of [...(model.processModels ?? []), ...(model.subprocessModels ?? [])]) {
    forEachCallHop(pm.body, (hop, node) => {
      if (inSubtree(hop.callee.modulePath)) {
        const leaf = hop.callee.interfacePath.slice(hop.callee.modulePath.length);
        const base = minimalRef(hop.callee.modulePath);
        const written = hop.callee.text.text;
        const newWritten = hop.callee.anonymous
          ? written.endsWith(".")
            ? `${base}.`
            : base
          : `${base}.${leaf.join(".")}`;
        rewriteSpan(hop.callee.text.source, newWritten);
      }
      // A `|` / `\` line's caller span IS the glyph token, and the glyph names
      // no module — it means "the step above me", which the move doesn't
      // change. Requalifying it would delete the glyph, silently rewriting an
      // implicit line as an explicit one on a plain re-parent.
      const callerIsWritten = node.owner.form === "explicit" || node.owner.form === "dist";
      if (callerIsWritten && hop.callerSource && inSubtree(hop.caller)) {
        rewriteSpan(hop.callerSource, minimalRef(hop.caller));
      }
    });
  }

  const walkDecls = (mods: readonly ResolvedModule[]): void => {
    for (const m of mods) {
      if (m !== target && m.decl.parentPath && inSubtree(m.decl.parentPath) && m.decl.parentSource) {
        rewriteSpan(m.decl.parentSource, minimalRef(m.decl.parentPath));
      }
      // Textually-qualified aspect refs only (suffix-form aspect refs fall to
      // the diagnostics safety net — engine-side plane resolution is not
      // re-run here).
      for (const a of m.decl.body.aspects) {
        const v = a.value;
        if (v && typeof v === "object" && "kind" in v && v.kind === "ref" && v.source) {
          if (inSubtree(v.path)) rewriteSpan(v.source, minimalRef(v.path));
        }
      }
      walkDecls(m.modules);
    }
  };
  walkDecls(model.modules);

  // --- delete at origin ------------------------------------------------------
  for (const e of removeMemberEdits(originSrc, extent.start.offset, extent.end.offset)) {
    push(originFile, e);
  }

  // --- insert at destination -------------------------------------------------
  const span = target.decl.source;
  const rawFragment = originSrc.slice(span.start.offset, span.end.offset);
  const outerIndent = leadingIndent(originSrc, span.start.offset);
  const fragment = rawFragment
    .split("\n")
    .map((line, i) => (i > 0 && line.startsWith(outerIndent) ? line.slice(outerIndent.length) : line))
    .join("\n");

  if (newParent) {
    const parentFile = newParent.decl.source?.file;
    if (!parentFile || !files.has(parentFile)) refuse("no-span", `destination file unknown`);
    const parentSrc = files.get(parentFile)!;
    for (const e of insertBodyMember(parentSrc, newParent.decl, fragment)) push(parentFile, e);
  } else {
    const sep = originSrc.endsWith("\n") ? "\n" : "\n\n";
    push(originFile, { start: originSrc.length, end: originSrc.length, newText: `${sep}${fragment}\n` });
  }

  return byFile;
}

// =============================================================================
// Stub promotion (§4a) — value ops on a stub compose promote-first
// =============================================================================

/**
 * Promote a synthesized stub (chain) into authored text, optionally carrying
 * one member line (the value op that triggered the promotion). Emits the full
 * missing chain as ONE nested fragment:
 *   - into the deepest AUTHORED ancestor's body when that ancestor's decl
 *     lives in the same file;
 *   - as an `in <Ancestor>`-prefixed top-level decl when the authored
 *     ancestor lives elsewhere;
 *   - as a plain top-level append for root stubs.
 * `src` must be the text of the stub's origin file (`decl.source.file` — the
 * file whose reference implied the stub).
 */
export function promoteStubWithMember(
  src: string,
  model: ResolvedModel,
  stubBoardId: string,
  memberLine?: string,
): TextEdit[] {
  const indexes = buildIndexes(model);
  const raw = stubBoardId.startsWith("#") ? stubBoardId.slice(1) : stubBoardId;
  const target = indexes.modulesById.get(raw as StableId) ?? indexes.modulesByPath.get(raw);
  if (!target) refuse("no-span", `no module '${stubBoardId}' in the model`);
  const path = indexes.pathOfModule.get(target);
  if (!path) refuse("no-span", `no path for module '${stubBoardId}'`);
  const originFile = target.decl.source?.file;

  // Deepest authored ancestor along the path (may be none for root stubs).
  let anchorLen = 0;
  let anchor: ResolvedModule | undefined;
  for (let i = path.length - 1; i >= 1; i--) {
    const candidate = indexes.modulesByPath.get(path.slice(0, i).join("."));
    if (candidate && !candidate.synthesized) {
      anchorLen = i;
      anchor = candidate;
      break;
    }
  }

  // The stub chain to author: everything below the anchor, down to the target.
  const chain: ResolvedModule[] = [];
  for (let i = anchorLen + 1; i <= path.length; i++) {
    const node = indexes.modulesByPath.get(path.slice(0, i).join("."));
    if (!node) refuse("no-span", `broken stub chain at '${path.slice(0, i).join(".")}'`);
    chain.push(node);
  }

  const anchorInFile = anchor && !isZeroSpan(anchor.decl.source) && anchor.decl.source?.file === originFile;
  if (anchorInFile && anchor) {
    return insertBodyMember(src, anchor.decl, renderChain(chain, memberLine, ""));
  }
  const prefix = anchor ? `in ${path.slice(0, anchorLen).join(".")} ` : "";
  const sep = src.endsWith("\n") ? "\n" : "\n\n";
  return [{ start: src.length, end: src.length, newText: `${sep}${prefix}${renderChain(chain, memberLine, "")}\n` }];
}

function isZeroSpan(span: SourceSpan | undefined): boolean {
  return !span || (span.start.offset === 0 && span.end.offset === 0);
}

/** Render the chain as a top-level nested fragment (no outer indent). */
function renderChain(chain: readonly ResolvedModule[], memberLine: string | undefined, indent: string): string {
  const node = chain[0]!;
  const kind = node.typeName || "module";
  const rest = chain.slice(1);
  const innerIndent = indent + INDENT;
  const inner =
    rest.length > 0
      ? renderChain(rest, memberLine, innerIndent)
      : memberLine !== undefined
        ? `${innerIndent}${memberLine}`
        : undefined;
  if (inner === undefined) return `${indent}${kind} ${node.name} {}`;
  return `${indent}${kind} ${node.name} {\n${inner}\n${indent}}`;
}
